import i18n from '../i18n/config';
import { useTranslation } from 'react-i18next';
import React, { useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Checkbox,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControl,
    FormLabel,
    Radio,
    RadioGroup,
    InputAdornment,
    FormControlLabel,
    Grid,
    IconButton,
    InputLabel,
    LinearProgress,
    MenuItem,
    Paper,
    Select,
    Stack,
    Tab,
    Tabs,
    Table,
    ToggleButton,
    ToggleButtonGroup,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import {
    Download as DownloadIcon,
    HelpOutline as HelpOutlineIcon,
    Print as PrintIcon,
    Refresh as RefreshIcon,
    Search as SearchIcon,
    WarningAmber as WarningIcon,
} from '@mui/icons-material';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import dayjs from 'dayjs';
import { api } from '../services/api';
import { useNotification } from '../components/common/GlobalNotifications';
import AIRecommendationsPanel from '../components/ai/AIRecommendationsPanel';
import AskAIButton from '../components/ai/AskAIButton';
import { useUserTimezone } from '../hooks/useUserTimezone';

const REPORT_TABS = [
    { key: 'executive', label: i18n.t('hardcoded.pages_tpmsreportspage.dashboard_ejecutivo') },
    { key: 'fuel', label: i18n.t('hardcoded.pages_tpmsreportspage.ahorro_sobrecosto_combustible') },
    { key: 'tire', label: i18n.t('hardcoded.pages_tpmsreportspage.desgaste_rendimiento_llanta') },
    { key: 'compliance', label: i18n.t('hardcoded.pages_tpmsreportspage.cumplimiento_presion') },
    { key: 'recurrence', label: i18n.t('hardcoded.pages_tpmsreportspage.reincidencia') },
    { key: 'resolution', label: i18n.t('hardcoded.pages_tpmsreportspage.tiempo_de_resolucion') },
    { key: 'overpressure', label: i18n.t('hardcoded.pages_tpmsreportspage.sobrepresion') },
    { key: 'temperature', label: i18n.t('hardcoded.components_alarmprofiles_profilecard.temperatura') },
    { key: 'operating', label: i18n.t('hardcoded.pages_tpmsreportspage.estado_operativo') },
];

const EXPORT_TYPE_BY_TAB = {
    executive: 'executive',
    fuel: i18n.t('hardcoded.pages_tpmsreportspage.fuel'),
    tire: 'tire',
    compliance: 'compliance',
    recurrence: 'recurrence',
    resolution: i18n.t('hardcoded.pages_tpmsreportspage.resolution'),
    overpressure: i18n.t('hardcoded.pages_tpmsreportspage.overpressure'),
    temperature: 'temperature',
    operating: 'operating',
};

const REPORT_CURRENCIES = [
    { code: 'USD', symbol: '$', name: i18n.t('hardcoded.pages_tpmsreportspage.us_dollar') },
    { code: 'EUR', symbol: 'EUR', name: i18n.t('hardcoded.pages_tpmreports.euro') },
    { code: 'MXN', symbol: '$', name: i18n.t('hardcoded.pages_tpmsreportspage.mexican_peso') },
    { code: 'COP', symbol: '$', name: i18n.t('hardcoded.pages_tpmsreportspage.colombian_peso') },
    { code: 'ARS', symbol: '$', name: i18n.t('hardcoded.pages_tpmsreportspage.argentine_peso') },
    { code: 'CLP', symbol: '$', name: i18n.t('hardcoded.pages_tpmsreportspage.chilean_peso') },
    { code: 'INR', symbol: 'Rs', name: i18n.t('hardcoded.pages_tpmreports.indian_rupee') },
    { code: 'VES', symbol: 'Bs', name: i18n.t('hardcoded.pages_tpmsreportspage.venezuelan_bolivar') },
];

const CONSUMPTION_UNITS = ['L/100km', 'km/L', 'MPG US', 'MPG UK', i18n.t('hardcoded.pages_tpmsreportspage.gal100mi'), i18n.t('hardcoded.pages_tpmsreportspage.kmgal')];
const CONSUMPTION_INTERVALS = [1, 10, 50, 100, 500, 1000];
const WORKDAYS = [
    { key: 0, label: i18n.t('hardcoded.pages_tpmreports.lun') },
    { key: 1, label: i18n.t('hardcoded.components_schematics_vehicletelemetrymodal.mar') },
    { key: 2, label: i18n.t('hardcoded.pages_tpmreports.mie') },
    { key: 3, label: i18n.t('hardcoded.pages_tpmreports.jue') },
    { key: 4, label: i18n.t('hardcoded.components_settings_notificationpreferenceseditor.vie') },
    { key: 5, label: i18n.t('hardcoded.pages_tpmreports.sab') },
    { key: 6, label: i18n.t('hardcoded.components_settings_notificationpreferenceseditor.dom') },
];

const NUMBER_FORMAT = new Intl.NumberFormat('es-MX', {
    maximumFractionDigits: 2,
});

const safeNumber = (value, fallback = 0) => {
    const n = Number(value);
    return Number.isFinite(n) ? n : fallback;
};

const formatNumber = (value, suffix = '') => `${NUMBER_FORMAT.format(safeNumber(value))}${suffix}`;
// Formato monetario: usamos `currencyDisplay: 'code'` para que SIEMPRE muestre el código
// (MXN, USD, COP, etc.) junto al monto — sin importar el locale del navegador.
// Ejemplo: "MXN 1,234.56" — claro y no ambiguo.
const formatCurrency = (value, currency = 'MXN') => {
    const code = (currency || 'MXN').toUpperCase();
    try {
        return new Intl.NumberFormat('es-MX', {
            style: 'currency',
            currency: code,
            currencyDisplay: 'code',
            maximumFractionDigits: 2,
        }).format(safeNumber(value));
    } catch {
        return `${code} ${safeNumber(value).toFixed(2)}`;
    }
};

const KPI_HELP = {
    ideal_state: i18n.t('hardcoded.pages_tpmsreportspage.resume_estado_de_presion_ideal'),
    critical_events: i18n.t('hardcoded.pages_tpmsreportspage.total_de_eventos_tpms_criticos'),
    diesel_cost: 'Sobrecosto diesel = penalizacion (deficit PSI x 0.2%) sobre litros base del vehiculo, escalada por horas operativas, kilometraje y precio diesel. El deficit se calcula contra la presion en frio detectada del viaje (NO contra el ajuste por temperatura).',
    potential_saving: i18n.t('hardcoded.pages_tpmsreportspage.ahorro_potencial_recuperable_sobrecosto_diesel'),
};

// Tooltips para las columnas clave del Reporte de Ahorro / Sobrecosto Combustible.
// Renderizadas en el header de la tabla via TooltipHeader.
const COLUMN_HELP = {
    psi_ideal_profile: i18n.t('hardcoded.pages_tpmsreportspage.presion_ideal_definida_en_el'),
    cold_pressure_start: i18n.t('hardcoded.pages_tpmsreportspage.presion_en_frio_detectada_al'),
    cold_pressure_current: i18n.t('hardcoded.pages_tpmsreportspage.presion_en_frio_estimada_vigente'),
    psi_vs_ideal: 'Diferencia entre la presion en frio estimada y la presion ideal del perfil. Negativo = falta presion; cero = ideal; positivo = sobre-inflado.',
    psi_ideal_variable: i18n.t('hardcoded.pages_tpmsreportspage.presion_ideal_ajustada_por_temperatura'),
    temp_pressure_rise: i18n.t('hardcoded.pages_tpmsreportspage.cuanto_subio_la_presion_real'),
    psi_deficit: i18n.t('hardcoded.pages_tpmsreportspage.deficit_real_para_penalizacion_max0'),
    inflation_detected: i18n.t('hardcoded.pages_tpmsreportspage.true_cuando_el_algoritmo_detecto'),
    psi_inflated_estimated: i18n.t('hardcoded.pages_tpmsreportspage.psi_acumulados_detectados_como_inflado'),
    cold_pressure_avg: 'Promedio (ponderado por horas) de la presion en frio estimada del vehiculo durante el periodo.',
    psi_vs_ideal_avg: i18n.t('hardcoded.pages_tpmsreportspage.promedio_ponderado_por_horas_de'),
    previous_timestamp: i18n.t('hardcoded.pages_tpmsreportspage.fechahora_de_la_lectura_anterior'),
    time_since_previous: i18n.t('hardcoded.pages_tpmsreportspage.tiempo_transcurrido_desde_la_lectura'),
    deficit_fraction: 'Fraccion directa de combustible extra por el deficit: deficit_PSI × 0.002 (equivalente a 0.2% por PSI). Es el multiplicador que se aplica a los litros base.',
    diesel_price_per_l: i18n.t('hardcoded.pages_tpmsreportspage.precio_del_diesel_por_litro'),
    km_per_month: i18n.t('hardcoded.pages_tpmsreportspage.kmmes_estimados_del_vehiculo_sale'),
    km_per_minute: i18n.t('hardcoded.pages_tpmsreportspage.kmmin_calendario_kmmes_30_24'),
    shifts_per_week: i18n.t('hardcoded.pages_tpmsreportspage.turnos_numero_de_dias_operativos'),
    hours_per_shift: i18n.t('hardcoded.pages_tpmsreportspage.horas_por_turno_segun_config'),
    vehicle_km_per_l: i18n.t('hardcoded.pages_tpmsreportspage.kml_base_del_vehiculo_campo'),
    km_per_l_lost: i18n.t('hardcoded.pages_tpmsreportspage.kml_perdido_kml_deficit_0002'),
    km_in_interval: i18n.t('hardcoded.pages_tpmsreportspage.km_recorridos_en_este_intervalo'),
    liters_lost: i18n.t('hardcoded.pages_tpmsreportspage.litros_perdidos_por_baja_presion'),
    cost_lost: i18n.t('hardcoded.pages_tpmsreportspage.mxn_perdido_litros_perdidos_mxnl'),
    // --- Desgaste / rendimiento de llanta ---
    cycle_id: 'Numero de ciclo/viaje detectado. Cambia cuando hay una pausa larga (>3h) y la llanta regresa a condiciones frias. Sirve para agrupar las lecturas de un mismo viaje.',
    performance_pct_table: i18n.t('hardcoded.pages_tpmsreportspage.rendimiento_de_la_llanta_segun'),
    performance_linear: 'Rendimiento LINEAL = presion_frio_estimada / ideal_perfil × 100. Metodo simple y transparente; ignora la curva real de desgaste.',
    performance_loss: 'Perdida de rendimiento = 100 − Rendimiento (tabla).',
    tire_loss_cost_interval: i18n.t('hardcoded.pages_tpmsreportspage.costo_de_desgaste_por_duracion'),
    tire_cost_per_tire: i18n.t('hardcoded.pages_tpmsreportspage.costo_por_llanta_tomado_del'),
    tire_total_km: 'Km totales que dura una llanta al 100% de rendimiento (filtro "Km Totales por Llanta"). Promedio que se aplica a cada posicion.',
    tire_cost_per_km_full: '$/km al 100% = Costo Llanta / Km Totales. Costo ideal por km si la llanta rindiera el 100%.',
    tire_cost_per_km_table: i18n.t('hardcoded.pages_tpmsreportspage.km_al_de_desgaste_tabla'),
    tire_cost_per_km_wasted_table: '$/km desperdiciado (tabla) = $/km al % desgaste − $/km al 100%. Sobrecosto por km causado por la baja presion.',
    tire_wasted_interval_table: '$/llanta desperdiciado en el intervalo (tabla) = $/km desperdiciado (tabla) × Km Intervalo.',
    tire_cost_per_km_linear: i18n.t('hardcoded.pages_tpmsreportspage.km_al_de_desgaste_lineal'),
    tire_cost_per_km_wasted_linear: '$/km desperdiciado (lineal) = $/km al % (lineal) − $/km al 100%.',
    tire_wasted_interval_linear: i18n.t('hardcoded.pages_tpmsreportspage.llanta_desperdiciado_en_el_intervalo'),
};

const toMillis = (value) => {
    const d = dayjs(value);
    return d.isValid() ? d.valueOf() : null;
};

const getUnitSymbol = (unit) => {
    const symbols = {
        PSI: 'PSI',
        BAR: 'bar',
        kPa: 'kPa',
        KPA: 'kPa',
        C: '°C',
        F: '°F',
        K: 'K',
        G: 'g',
        'm/s2': 'm/s²',
        'ft/s2': 'ft/s²',
        'km/h': 'km/h',
        mph: 'mph',
        knots: 'kn',
    };
    return symbols[unit] || unit;
};

const convertPressureFromPSI = (valueInPSI, pressureUnit) => {
    if (valueInPSI === null || valueInPSI === undefined) return null;
    return api.profileUtils.convertPressure(valueInPSI, 'PSI', pressureUnit || 'PSI');
};

const convertTemperatureFromC = (valueInC, temperatureUnit) => {
    if (valueInC === null || valueInC === undefined) return null;
    return api.profileUtils.convertTemperature(valueInC, 'C', temperatureUnit || 'C');
};

const flattenVehicles = (vehicles = []) => {
    const result = [];

    const walk = (items = []) => {
        items.forEach((vehicle) => {
            result.push(vehicle);
            if (vehicle.child_vehicles?.length) {
                walk(vehicle.child_vehicles);
            }
        });
    };

    walk(vehicles);
    return result;
};

// CSV export presets. The decimal separator MUST match the user's Excel
// locale, otherwise Excel reads "94.64" as the integer 9464 (treating the
// dot as a thousands separator) and the decimals vanish. The column
// delimiter must differ from the decimal separator, hence ';' for comma.
const CSV_FORMATS = {
    es: { key: 'es', label: i18n.t('hardcoded.pages_tpmsreportspage.excel_en_espanol'), hint: 'Decimal con coma (94,64) · columnas con punto y coma (;)', decimal: ',', delimiter: ';' },
    en: { key: 'en', label: i18n.t('hardcoded.pages_tpmsreportspage.excel_en_ingles'), hint: i18n.t('hardcoded.pages_tpmsreportspage.decimal_con_punto_9464_columnas'), decimal: '.', delimiter: ',' },
    tsv: { key: 'tsv', label: i18n.t('hardcoded.pages_tpmsreportspage.tabulado_tsv'), hint: i18n.t('hardcoded.pages_tpmsreportspage.decimal_con_coma_9464_columnas'), decimal: ',', delimiter: '\t' },
};
const DEFAULT_CSV_FORMAT = 'es';
const PLAIN_NUMBER_RE = /^-?\d+(\.\d+)?$/;

const serializeCsvCell = (value, { delimiter = ',', decimal = '.' } = {}) => {
    if (value === null || value === undefined) return '';
    let raw = value;
    // Booleans render as "Si" / "No" in Spanish — the report is es-MX.
    if (typeof raw === 'boolean') raw = raw ? i18n.t('hardcoded.pages_tpmreports.si') : i18n.t('hardcoded.pages_tpmreports.no');
    if (typeof raw === 'number' && Number.isFinite(raw)) {
        raw = String(raw);
        if (decimal !== '.') raw = raw.replace('.', decimal);
    } else {
        raw = String(raw);
        // Only swap the decimal point on values that are purely a number
        // (e.g. "94.64") — never on dates ("15/5/26") or labels.
        if (decimal !== '.' && PLAIN_NUMBER_RE.test(raw)) raw = raw.replace('.', decimal);
    }
    if (raw.includes(delimiter) || raw.includes('\n') || raw.includes('\r') || raw.includes('"')) {
        return `"${raw.replace(/"/g, '""')}"`;
    }
    return raw;
};

// Extract the plain-text CSV value for a column cell. Order of preference:
//   1) column.csv(value, row) — explicit text formatter for CSV output.
//   2) column.render(value, row) — but ONLY if it returns a string/number
//      (it could return JSX, e.g. a Chip — in that case we fall through).
//   3) the raw value from the row.
// This way the CSV always matches what the user sees in the table when the
// table render is plain text, and degrades gracefully for JSX renders.
const csvValueForColumn = (col, row) => {
    const rawValue = row?.[col.key];
    if (typeof col.csv === 'function') {
        try {
            const out = col.csv(rawValue, row);
            return out === undefined ? '' : out;
        } catch {
            return rawValue;
        }
    }
    if (typeof col.render === 'function') {
        try {
            const out = col.render(rawValue, row);
            if (typeof out === 'string' || typeof out === 'number') return out;
            if (typeof out === 'boolean') return out ? i18n.t('hardcoded.pages_tpmreports.si') : i18n.t('common.no');
            // JSX / object — fall through to raw value.
        } catch {
            // ignore — fall through to raw value
        }
    }
    return rawValue;
};

// Build a CSV from the table's row data using either explicit column
// definitions (preferred — the user gets the same Spanish labels and the
// same formatted values shown on screen) or, as a fallback, the union of
// row keys with raw values.
const tableToCsv = (rows = [], columns = null, format = CSV_FORMATS[DEFAULT_CSV_FORMAT]) => {
    if (!rows.length) return '';

    const { delimiter } = format;
    const cell = (value) => serializeCsvCell(value, format);

    if (Array.isArray(columns) && columns.length) {
        const cols = columns.filter((col) => col && col.key);
        const headerLine = cols.map((c) => cell(c.label || c.key)).join(delimiter);
        const lines = [headerLine];
        rows.forEach((row) => {
            lines.push(cols.map((c) => cell(csvValueForColumn(c, row))).join(delimiter));
        });
        return lines.join('\r\n');
    }

    const seen = new Set();
    const fallbackKeys = [];
    rows.forEach((row) => {
        Object.keys(row || {}).forEach((key) => {
            if (!seen.has(key)) {
                seen.add(key);
                fallbackKeys.push(key);
            }
        });
    });
    const headerLine = fallbackKeys.map((k) => cell(k)).join(delimiter);
    const lines = [headerLine];
    rows.forEach((row) => {
        lines.push(fallbackKeys.map((k) => cell(row?.[k])).join(delimiter));
    });
    return lines.join('\r\n');
};


const downloadTextFile = (content, fileName, mime = 'text/csv;charset=utf-8') => {
    // Prepend a UTF-8 BOM so Excel (incl. Excel for Mac) detects the encoding
    // and renders °C / accents correctly instead of mojibake (¬∞C).
    const blob = new Blob(['\ufeff', content], { type: mime });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
};

const KPIGrid = ({ items }) => (
    <Grid container spacing={2} sx={{ mb: 2 }}>
        {(items || []).map((item) => (
            <Grid item xs={12} sm={6} md={3} key={item.label}>
                <Card sx={{ height: '100%', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                    <CardContent>
                        <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mb: 1 }}>
                            <Typography variant="body2" color="text.secondary">
                                {item.label}
                            </Typography>
                            {item.helpKey && (
                                <Tooltip
                                    title={<Typography variant="caption" sx={{ whiteSpace: 'pre-line' }}>{KPI_HELP[item.helpKey] || item.helpText || ''}</Typography>}
                                    arrow
                                >
                                    <HelpOutlineIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
                                </Tooltip>
                            )}
                        </Stack>
                        <Typography variant="h5" fontWeight={700} color={item.color || 'primary.main'}>
                            {item.value}
                        </Typography>
                        {item.subtitle && <Typography variant="caption" color="text.secondary">{item.subtitle}</Typography>}
                    </CardContent>
                </Card>
            </Grid>
        ))}
    </Grid>
);

const SemaforoChip = ({ status }) => {
  const { t } = useTranslation();
    const normalized = String(status || '').toLowerCase();
    if (normalized.includes(t('hardcoded.pages_tpmsreportspage.no_debe'))) {
        return <Chip color="error" label={t('hardcoded.pages_tpmsreportspage.no_debe_salir')} />;
    }
    if (normalized.includes('observacion')) {
        return <Chip color="warning" label={t('hardcoded.pages_tpmsreportspage.sale_con_observacion')} />;
    }
    return <Chip color="success" label={t('hardcoded.pages_tpmsreportspage.puede_salir')} />;
};

const DataTable = ({
    tableKey = 'default',
    title,
    columns,
    rows,
    searchPlaceholder,
    onExportCsv,
    defaultSort = null,
}) => {
  const { t } = useTranslation();
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(15);
    const [search, setSearch] = useState('');
    const [sortBy, setSortBy] = useState(defaultSort?.key || '');
    const [sortOrder, setSortOrder] = useState(defaultSort?.order || t('hardcoded.components_common_advanceddatatable.desc'));
    const [visibleColumns, setVisibleColumns] = useState(() => {
        try {
            const saved = localStorage.getItem(`tpms_report_cols_${tableKey}`);
            if (!saved) return columns.map((c) => c.key);
            const parsed = JSON.parse(saved);
            return Array.isArray(parsed) && parsed.length ? parsed : columns.map((c) => c.key);
        } catch {
            return columns.map((c) => c.key);
        }
    });

    useEffect(() => {
        localStorage.setItem(`tpms_report_cols_${tableKey}`, JSON.stringify(visibleColumns));
    }, [tableKey, visibleColumns]);

    const activeColumns = useMemo(() => {
        const selected = columns.filter((c) => visibleColumns.includes(c.key));
        return selected.length ? selected : columns;
    }, [columns, visibleColumns]);

    const filteredRows = useMemo(() => {
        const query = search.trim().toLowerCase();

        const afterSearch = !query
            ? rows || []
            : (rows || []).filter((row) =>
                activeColumns.some((col) => String(row?.[col.key] ?? '').toLowerCase().includes(query))
            );

        if (!sortBy) return afterSearch;

        const sorted = [...afterSearch].sort((a, b) => {
            const av = a?.[sortBy];
            const bv = b?.[sortBy];

            if (typeof av === 'number' && typeof bv === 'number') {
                return av - bv;
            }

            return String(av ?? '').localeCompare(String(bv ?? ''), 'es');
        });

        return sortOrder === 'asc' ? sorted : sorted.reverse();
    }, [rows, search, activeColumns, sortBy, sortOrder]);

    const pagedRows = useMemo(() => {
        const start = page * rowsPerPage;
        return filteredRows.slice(start, start + rowsPerPage);
    }, [filteredRows, page, rowsPerPage]);

    const toggleSort = (key) => {
        if (sortBy === key) {
            setSortOrder((prev) => (prev === 'asc' ? t('hardcoded.components_common_advanceddatatable.desc') : t('hardcoded.components_common_advanceddatatable.asc')));
            return;
        }
        setSortBy(key);
        setSortOrder(t('hardcoded.components_common_advanceddatatable.desc'));
    };

    useEffect(() => {
        setPage(0);
    }, [search, rowsPerPage, rows]);

    return (
        <Paper sx={{ p: 2 }}>
            <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'center' }} spacing={2} sx={{ mb: 2 }}>
                <Typography variant="h6" fontWeight={700}>{title}</Typography>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                    <TextField
                        size="small"
                        value={search}
                        onChange={(event) => setSearch(event.target.value)}
                        placeholder={searchPlaceholder || t('hardcoded.pages_tpmsreportspage.buscar')}
                        InputProps={{ startAdornment: <SearchIcon sx={{ mr: 1 }} fontSize="small" /> }}
                    />
                    <Button
                        variant="outlined"
                        startIcon={<DownloadIcon />}
                        onClick={() => onExportCsv?.(filteredRows, columns)}
                    >
                        {i18n.t('audit.export.csv')}</Button>
                </Stack>
            </Stack>

            <Stack direction="row" spacing={0.5} sx={{ mb: 1, flexWrap: 'wrap' }}>
                {columns.map((col) => {
                    const selected = visibleColumns.includes(col.key);
                    return (
                        <Chip
                            key={`col-${tableKey}-${col.key}`}
                            size="small"
                            label={col.label}
                            color={selected ? 'primary' : 'default'}
                            variant={selected ? 'filled' : 'outlined'}
                            onClick={() => {
                                if (selected && visibleColumns.length === 1) return;
                                setVisibleColumns((prev) => selected ? prev.filter((k) => k !== col.key) : [...prev, col.key]);
                            }}
                        />
                    );
                })}
            </Stack>

            <TableContainer sx={{ maxHeight: 520 }}>
                <Table size="small" stickyHeader>
                    <TableHead>
                        <TableRow>
                            {activeColumns.map((column) => (
                                <TableCell
                                    key={column.key}
                                    onClick={() => column.sortable !== false && toggleSort(column.key)}
                                    sx={{
                                        fontWeight: 700,
                                        cursor: column.sortable === false ? 'default' : 'pointer',
                                        whiteSpace: 'nowrap',
                                        backgroundColor: 'error.main',
                                        color: 'common.white',
                                        '&.MuiTableCell-stickyHeader': {
                                            backgroundColor: 'error.main',
                                            color: 'common.white',
                                        },
                                    }}
                                >
                                    {column.help ? (
                                        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ display: 'inline-flex' }}>
                                            <span>{column.label}</span>
                                            <Tooltip
                                                arrow
                                                title={<Typography variant="caption" sx={{ whiteSpace: 'pre-line' }}>{column.help}</Typography>}
                                            >
                                                {/* The icon stops click propagation so reading the help
                                                    text doesn't accidentally toggle the column sort. */}
                                                <HelpOutlineIcon
                                                    onClick={(e) => e.stopPropagation()}
                                                    sx={{ fontSize: 14, color: 'rgba(255,255,255,0.85)', cursor: 'help' }}
                                                />
                                            </Tooltip>
                                        </Stack>
                                    ) : (
                                        column.label
                                    )}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {pagedRows.map((row, idx) => (
                            <TableRow key={`${title}-${idx}`} hover>
                                {activeColumns.map((column) => (
                                    <TableCell key={`${column.key}-${idx}`}>
                                        {column.render ? column.render(row?.[column.key], row) : String(row?.[column.key] ?? '-')}
                                    </TableCell>
                                ))}
                            </TableRow>
                        ))}
                        {!pagedRows.length && (
                            <TableRow>
                                <TableCell colSpan={activeColumns.length}>
                                    <Alert severity="info">{t('hardcoded.pages_tpmsreportspage.sin_datos_para_los_filtros')}</Alert>
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
            <TablePagination
                component="div"
                count={filteredRows.length}
                page={page}
                onPageChange={(_, value) => setPage(value)}
                rowsPerPage={rowsPerPage}
                rowsPerPageOptions={[10, 15, 25, 50]}
                onRowsPerPageChange={(event) => setRowsPerPage(Number(event.target.value))}
            />
        </Paper>
    );
};

const TPMSReportsPage = () => {
  const { t } = useTranslation();
    const { showError, showSuccess } = useNotification();
    const { formatUserDateTimeSync } = useUserTimezone();

    const [loading, setLoading] = useState(false);
    const [exporting, setExporting] = useState(false);
    // Export-format dialog: `run` is a thunk that performs the actual export
    // once the user picks a CSV format. Lets every export button (the server
    // "Oficial" one and each table's local export) share one dialog.
    const [exportDialog, setExportDialog] = useState({ open: false, run: null });
    const [exportFormat, setExportFormat] = useState(() => {
        try {
            const saved = localStorage.getItem('tpms_export_format');
            if (saved && CSV_FORMATS[saved]) return saved;
        } catch (e) { /* ignore */ }
        return DEFAULT_CSV_FORMAT;
    });
    const [tab, setTab] = useState('executive');
    const [vehicles, setVehicles] = useState([]);
    const [report, setReport] = useState(null);
    const [currencyWarning, setCurrencyWarning] = useState('');
    const [unitPreferences, setUnitPreferences] = useState({
        pressure_unit: 'PSI',
        temperature_unit: 'C',
        acceleration_unit: 'G',
        speed_unit: 'km/h',
        decimal_places: 2,
        decimal_places_map: {},
    });

    const [filters, setFilters] = useState({
        start_date: dayjs().subtract(30, 'day').format('YYYY-MM-DD'),
        end_date: dayjs().format('YYYY-MM-DD'),
        vehicle_ids: [],
        ticket_status: '',
        breach_type: '',
        metric_type: '',
        diesel_price: 24,
        base_liters_per_100km: 35,
        estimated_km: '',
        tire_replacement_cost: 12000,
        tire_total_km: 150000,
        currency: 'MXN',
        consumption_value: 35,
        consumption_unit: 'L/100km',
        consumption_interval_km: 100,
    });

    const [operationalConfig, setOperationalConfig] = useState({
        workdays: [0, 1, 2, 3, 4, 5],
        hours_per_day: 8,
        shift_mode: 'single',
    });

    const [currencyRatesDisplay, setCurrencyRatesDisplay] = useState({
        USD: '', EUR: '', COP: '', ARS: '', CLP: '', INR: '', VES: '',
    });
    const [showRatesPanel, setShowRatesPanel] = useState(false);

    const [dieselViewFilters, setDieselViewFilters] = useState({
        company: '',
        vehicle: '',
        sensor: '',
        axle: '',
        position: '',
        alarm_profile: '',
        severity: '',
        state_mode: 'all',
    });

    const loadVehicles = async () => {
        try {
            const response = await api.vehicles.getAll();
            const list = flattenVehicles(response?.data || []);
            setVehicles(list);
        } catch (error) {
            showError(t('hardcoded.pages_tpmsreportspage.no_fue_posible_cargar_los'));
        }
    };

    const loadUnitPreferences = async () => {
        try {
            const response = await api.unitPreferences.getMyPreferences();
            if (response?.data) {
                setUnitPreferences(response.data);
                const reportPrefs = response.data.report_preferences || {};
                if (reportPrefs.currency || reportPrefs.consumption || reportPrefs.tables || reportPrefs.filters) {
                    const savedFilters = reportPrefs.filters || {};
                    setFilters((prev) => ({
                        ...prev,
                        currency: reportPrefs.currency || prev.currency,
                        consumption_unit: reportPrefs.consumption?.unit || prev.consumption_unit,
                        consumption_interval_km: reportPrefs.consumption?.interval_km || prev.consumption_interval_km,
                        // Filtros guardados por usuario (con guard ante valores inválidos)
                        diesel_price: typeof savedFilters.diesel_price === 'number' ? savedFilters.diesel_price : prev.diesel_price,
                        tire_replacement_cost: typeof savedFilters.tire_replacement_cost === 'number' ? savedFilters.tire_replacement_cost : prev.tire_replacement_cost,
                        consumption_value: typeof savedFilters.consumption_value === 'number' ? savedFilters.consumption_value : prev.consumption_value,
                        estimated_km: savedFilters.estimated_km != null && savedFilters.estimated_km !== '' ? savedFilters.estimated_km : prev.estimated_km,
                        ticket_status: typeof savedFilters.ticket_status === 'string' ? savedFilters.ticket_status : prev.ticket_status,
                        metric_type: typeof savedFilters.metric_type === 'string' ? savedFilters.metric_type : prev.metric_type,
                    }));
                }
            }
        } catch (error) {
            // Mantener fallback por defecto sin bloquear la vista
        }
    };

    const loadReportSettings = async () => {
        try {
            const response = await api.reports.getTPMSSettings();
            const settings = response?.data || {};
            if (settings.tpms_report_operational_config) {
                setOperationalConfig((prev) => ({ ...prev, ...settings.tpms_report_operational_config }));
            }
            if (settings.tpms_currency_rates) {
                const display = {};
                for (const [code, rate] of Object.entries(settings.tpms_currency_rates)) {
                    const num = safeNumber(rate, 0);
                    if (num > 0) display[code] = String(Math.round((1 / num) * 10000) / 10000);
                }
                setCurrencyRatesDisplay((prev) => ({ ...prev, ...display }));
            }
        } catch {
            // fallback local
        }
    };

    const loadReport = async () => {
        setLoading(true);
        api.systemUsage.track('reports', 'opened', { path: '/reports' });
        try {
            const payload = {
                ...filters,
                vehicle_ids: filters.vehicle_ids.length ? filters.vehicle_ids.join(',') : undefined,
                estimated_km: filters.estimated_km === '' ? undefined : safeNumber(filters.estimated_km, undefined),
                consumption_interval_km: safeNumber(filters.consumption_interval_km, 100),
                operational_config: JSON.stringify({
                    workdays: operationalConfig.workdays || [0, 1, 2, 3, 4, 5],
                    hours_per_day: safeNumber(operationalConfig.hours_per_day, 8),
                    shift_mode: operationalConfig.shift_mode || 'single',
                }),
            };

            const response = await api.reports.getTPMSOverview(payload);
            setReport(response?.data || null);
            const warning = response?.data?.currency?.warning || '';
            setCurrencyWarning(warning);
            if (warning) setShowRatesPanel(true);
        } catch (error) {
            showError(error?.response?.data?.detail || t('hardcoded.pages_tpmsreportspage.error_cargando_reportes_tpms'));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadVehicles();
        loadUnitPreferences();
        loadReportSettings();
    }, []);

    useEffect(() => {
        loadReport();
    }, []);

    const handleFilterChange = (key, value) => {
        setFilters((prev) => ({ ...prev, [key]: value }));
    };

    const persistReportPreferences = async () => {
        try {
            await api.unitPreferences.updateMyPreferences({
                report_preferences: {
                    currency: filters.currency,
                    consumption: {
                        unit: filters.consumption_unit,
                        interval_km: safeNumber(filters.consumption_interval_km, 100),
                    },
                    tables: {
                        density: t('hardcoded.pages_gpspage.compact'),
                    },
                    filters: {
                        diesel_price: typeof filters.diesel_price === 'number' ? filters.diesel_price : safeNumber(filters.diesel_price, 24),
                        tire_replacement_cost: typeof filters.tire_replacement_cost === 'number' ? filters.tire_replacement_cost : safeNumber(filters.tire_replacement_cost, 12000),
                        consumption_value: typeof filters.consumption_value === 'number' ? filters.consumption_value : safeNumber(filters.consumption_value, 35),
                        estimated_km: filters.estimated_km,
                        ticket_status: filters.ticket_status || '',
                        metric_type: filters.metric_type || '',
                    },
                },
            });
        } catch {
            // sin bloqueo
        }
    };

    const persistOperationalConfig = async () => {
        try {
            await api.reports.updateTPMSSettings({
                tpms_report_operational_config: operationalConfig,
            });
        } catch {
            // sin bloqueo
        }
    };

    const saveCurrencyRates = async () => {
        try {
            const rates = {};
            for (const [code, val] of Object.entries(currencyRatesDisplay)) {
                const num = safeNumber(val, 0);
                if (num > 0) rates[code] = 1 / num;
            }
            await api.reports.updateTPMSSettings({ tpms_currency_rates: rates });
            showSuccess(t('hardcoded.pages_tpmsreportspage.tipo_de_cambio_guardado'));
            setShowRatesPanel(false);
            loadReport();
        } catch {
            showError(t('hardcoded.pages_tpmsreportspage.error_al_guardar_tasas_de'));
        }
    };

    const handleApplyFilters = () => {
        persistReportPreferences();
        persistOperationalConfig();
        loadReport();
    };

    const handleResetFilters = () => {
        const reset = {
            start_date: dayjs().subtract(30, 'day').format('YYYY-MM-DD'),
            end_date: dayjs().format('YYYY-MM-DD'),
            vehicle_ids: [],
            ticket_status: '',
            breach_type: '',
            metric_type: '',
            diesel_price: 24,
            base_liters_per_100km: 35,
            estimated_km: '',
            tire_replacement_cost: 12000,
            tire_total_km: 150000,
            currency: 'MXN',
            consumption_value: 35,
            consumption_unit: 'L/100km',
            consumption_interval_km: 100,
        };
        setFilters(reset);
        setTimeout(() => {
            loadReport();
        }, 0);
    };

    // Open the shared format dialog, then run `action(format)` with the chosen
    // CSV format once the user confirms.
    const requestExport = (action) => setExportDialog({ open: true, run: action });
    const closeExportDialog = () => setExportDialog({ open: false, run: null });
    const confirmExport = () => {
        try { localStorage.setItem('tpms_export_format', exportFormat); } catch (e) { /* ignore */ }
        const action = exportDialog.run;
        const format = CSV_FORMATS[exportFormat] || CSV_FORMATS[DEFAULT_CSV_FORMAT];
        closeExportDialog();
        if (typeof action === 'function') action(format);
    };

    const exportCurrentTab = (format) => requestExport((fmt) => runServerExport(fmt));

    const runServerExport = async (format) => {
        try {
            setExporting(true);
            const payload = {
                ...filters,
                vehicle_ids: filters.vehicle_ids.length ? filters.vehicle_ids.join(',') : undefined,
                estimated_km: filters.estimated_km === '' ? undefined : safeNumber(filters.estimated_km, undefined),
                consumption_interval_km: safeNumber(filters.consumption_interval_km, 100),
                operational_config: JSON.stringify({
                    workdays: operationalConfig.workdays || [0, 1, 2, 3, 4, 5],
                    hours_per_day: safeNumber(operationalConfig.hours_per_day, 8),
                    shift_mode: operationalConfig.shift_mode || 'single',
                }),
                report_type: EXPORT_TYPE_BY_TAB[tab],
                csv_delimiter: format.delimiter,
                csv_decimal: format.decimal,
            };

            const response = await api.reports.exportTPMS(payload);
            const filename = `tpms_${payload.report_type}_${filters.start_date}_${filters.end_date}.csv`;
            // Backend already prepends the UTF-8 BOM; just declare the charset.
            const url = window.URL.createObjectURL(new Blob([response.data], { type: 'text/csv;charset=utf-8' }));
            const a = document.createElement('a');
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
            showSuccess(t('hardcoded.pages_tpmsreportspage.reporte_exportado'));
        } catch (error) {
            showError(t('hardcoded.pages_tpmsreportspage.no_se_pudo_exportar_el'));
        } finally {
            setExporting(false);
        }
    };

    const executiveKpis = useMemo(() => {
        const k = report?.executive_dashboard?.kpis || {};
        const ccy = report?.currency?.target_currency || filters.currency || 'MXN';
        const psiAlarms = safeNumber(k.psi_alarms_period, 0);
        const tempAlarms = safeNumber(k.temp_alarms_period, 0);
        const totalAlarms = psiAlarms + tempAlarms;
        return [
            { label: t('hardcoded.pages_tpmsreportspage.vehiculos_monitoreados'), value: formatNumber(k.total_vehicles_monitored), color: 'primary.main' },
            { label: t('hardcoded.pages_tpmsreportspage.sensores_activos'), value: formatNumber(k.total_active_sensors), color: 'primary.main' },
            { label: t('hardcoded.pages_tpmsreportspage.llantas_monitoreadas'), value: formatNumber(k.total_tires_monitored), color: 'primary.main' },
            { label: t('hardcoded.pages_tpmsreportspage.llantas_en_ideal'), value: formatNumber(k.pct_tires_ideal, '%'), color: 'success.main' },
            {
                label: t('hardcoded.pages_tpmsreportspage.alarmas_creadas'),
                value: formatNumber(totalAlarms),
                subtitle: `${formatNumber(psiAlarms)} PSI / ${formatNumber(tempAlarms)} TEMP`,
                color: 'error.main',
            },
            { label: t('hardcoded.pages_tpmreports.tiempo_promedio_resolucion_min'), value: formatNumber(k.avg_resolution_minutes), color: 'info.main' },
            { label: t('hardcoded.pages_clientcalculatepage.litros_extra_estimados'), value: formatNumber(k.estimated_extra_liters), color: 'warning.main' },
            { label: t('hardcoded.pages_tpmsreportspage.sobrecosto_combustible'), value: formatCurrency(k.estimated_extra_diesel_cost, ccy), color: 'warning.main', helpKey: 'diesel_cost' },
            { label: t('hardcoded.pages_tpmsreportspage.perdida_rendimiento_llanta'), value: formatCurrency(k.estimated_tire_loss_cost, ccy), color: 'error.main' },
            { label: t('hardcoded.pages_tpmsreportspage.ahorro_potencial'), value: formatCurrency(k.potential_saving, ccy), color: 'success.main', helpKey: 'potential_saving' },
        ];
    }, [report, filters.currency]);

    const reportCurrency = report?.currency?.target_currency || filters.currency || 'MXN';

    const pressureUnit = unitPreferences?.pressure_unit || 'PSI';
    const temperatureUnit = unitPreferences?.temperature_unit || 'C';
    const pressureSymbol = getUnitSymbol(pressureUnit);
    const temperatureSymbol = getUnitSymbol(temperatureUnit);
    const decimalMap = unitPreferences?.decimal_places_map || {};
    const globalDecimals = Number.isInteger(unitPreferences?.decimal_places)
        ? unitPreferences.decimal_places
        : parseInt(unitPreferences?.decimal_places, 10) || 2;
    const pressureDecimals = Number.isInteger(decimalMap.pressure) ? decimalMap.pressure : globalDecimals;
    const temperatureDecimals = Number.isInteger(decimalMap.temperature) ? decimalMap.temperature : 1;

    // Backend stores all pressures as PSI. This formatter converts to the
    // user's preferred unit (bar / kPa / PSI) so it stays consistent with
    // formatPressureReal / formatPressureDelta. Before this fix the column
    // header read "(bar)" but the cell printed the raw PSI number — a >10×
    // visual error for non-PSI users.
    const formatPressureFromStored = (value) => {
        const numeric = safeNumber(value, NaN);
        if (!Number.isFinite(numeric)) return '-';
        const converted = convertPressureFromPSI(numeric, pressureUnit);
        return converted === null ? '-' : `${safeNumber(converted).toFixed(pressureDecimals)} ${pressureSymbol}`;
    };

    const formatPressureReal = (value) => {
        const numeric = safeNumber(value, NaN);
        if (!Number.isFinite(numeric)) return '-';
        const converted = convertPressureFromPSI(numeric, pressureUnit);
        return converted === null ? '-' : `${safeNumber(converted).toFixed(pressureDecimals)} ${pressureSymbol}`;
    };

    const formatPressureDelta = (value) => {
        const numeric = safeNumber(value, NaN);
        if (!Number.isFinite(numeric)) return '-';
        const converted = convertPressureFromPSI(numeric, pressureUnit);
        return converted === null ? '-' : `${safeNumber(converted).toFixed(pressureDecimals)} ${pressureSymbol}`;
    };

    const formatTemperatureValue = (value) => {
        const cValue = safeNumber(value, NaN);
        if (!Number.isFinite(cValue)) return '-';
        const converted = convertTemperatureFromC(cValue, temperatureUnit);
        return converted === null ? '-' : `${safeNumber(converted).toFixed(temperatureDecimals)} ${temperatureSymbol}`;
    };

    // Small per-km / per-interval money values need more than 2 decimals to be
    // meaningful (e.g. $/km wasted = 0.0016). Show 4-6 fractional digits.
    const formatPerKm = (value) => {
        const n = safeNumber(value, NaN);
        if (!Number.isFinite(n)) return '-';
        return n.toLocaleString('es-MX', { minimumFractionDigits: 4, maximumFractionDigits: 6 });
    };

    const formatUserDateTime = (value) => {
        if (!value) return '-';
        try {
            return formatUserDateTimeSync(value, {
                year: 'numeric',
                month: 'short',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
                timeZoneName: 'short',
            });
        } catch {
            return String(value);
        }
    };

    // CSV-friendly datetime: Google Sheets / Excel parse "YYYY-MM-DD HH:mm:ss"
    // natively as a datetime. The localized string used in the table (e.g.
    // "22 abr 2026, 20:26 GMT-4") gets mis-parsed and ends up as
    // "12/05/2026 0:00:00" — that's why this version exists.
    const formatCsvDateTime = (value) => {
        if (!value) return '';
        const d = dayjs(value);
        if (!d.isValid()) return String(value);
        return d.format('YYYY-MM-DD HH:mm:ss');
    };

    // CSV cell formatters that mirror the table's `render` formatters but
    // return a PURE NUMBER (no unit suffix, no currency code). The unit is
    // already in the column header, so this keeps Sheets / Excel happy —
    // sums, averages, sorting all work because the cell is numeric.
    const csvFromNumber = (value, decimals) => {
        const n = safeNumber(value, NaN);
        if (!Number.isFinite(n)) return '';
        return n.toFixed(decimals);
    };
    // CSV mirror of formatPressureFromStored — converts PSI to the user's unit.
    const csvPressureFromStored = (value) => {
        const n = safeNumber(value, NaN);
        if (!Number.isFinite(n)) return '';
        const c = convertPressureFromPSI(n, pressureUnit);
        return c === null ? '' : safeNumber(c).toFixed(pressureDecimals);
    };
    const csvPressureReal = (value) => {
        const n = safeNumber(value, NaN);
        if (!Number.isFinite(n)) return '';
        const c = convertPressureFromPSI(n, pressureUnit);
        return c === null ? '' : safeNumber(c).toFixed(pressureDecimals);
    };
    const csvPressureDelta = csvPressureReal;
    const csvTemperatureValue = (value) => {
        const n = safeNumber(value, NaN);
        if (!Number.isFinite(n)) return '';
        const c = convertTemperatureFromC(n, temperatureUnit);
        return c === null ? '' : safeNumber(c).toFixed(temperatureDecimals);
    };
    const csvCurrency = (value) => csvFromNumber(value, 2);
    const csvPercent = (value) => csvFromNumber(value, 2);
    const csvNumber = (value) => csvFromNumber(value, 2);

    const trendData = report?.executive_dashboard?.trend || [];
    const fuelRows = report?.fuel_report?.rows || [];
    const fuelDetailRows = report?.fuel_report?.detail_rows || [];
    const fuelSensorRows = report?.fuel_report?.sensor_rows || [];
    const fuelAxleRows = report?.fuel_report?.axle_rows || [];
    const fuelPositionRows = report?.fuel_report?.position_rows || [];
    const fuelTrendRows = report?.fuel_report?.trend || [];
    const fuelChart = report?.fuel_report?.chart || {};
    const tireRows = report?.tire_performance_report?.rows || [];
    const tireDetailRows = report?.tire_performance_report?.detail_rows || [];
    const complianceRows = report?.pressure_compliance_report?.by_vehicle || [];
    const recurrence = report?.recurrence_report || {};
    const resolution = report?.resolution_report || {};
    const overpressureRows = report?.overpressure_report?.rows || [];
    const temperatureEvents = report?.temperature_report?.events || [];
    const temperatureStress = report?.temperature_report?.vehicle_stress || [];
    const operatingRows = report?.operating_status_report?.rows || [];

    const exportLocalRows = (rows, filenamePrefix, columns = null) => {
        if (!rows || !rows.length) {
            showError(t('hardcoded.pages_tpmsreportspage.no_hay_datos_para_exportar'));
            return;
        }
        // Ask the user how they want the CSV (decimal separator / delimiter)
        // before building it, so it opens cleanly in their Excel locale.
        requestExport((format) => {
            // Use column definitions when available so the CSV headers match the
            // Spanish labels shown in the table (e.g. "Presion Frio Inicio Viaje"
            // instead of "cold_pressure_start").
            const csv = tableToCsv(rows, columns, format);
            if (!csv) {
                showError(t('hardcoded.pages_tpmsreportspage.no_hay_datos_para_exportar'));
                return;
            }
            downloadTextFile(csv, `${filenamePrefix}_${dayjs().format('YYYYMMDD_HHmmss')}.csv`);
            showSuccess(t('hardcoded.pages_tpmsreportspage.csv_generado'));
        });
    };

    const dieselFilterOptions = useMemo(() => {
        const companies = [...new Set(fuelDetailRows.map((r) => r.company).filter(Boolean))].sort((a, b) => String(a).localeCompare(String(b), 'es'));
        const vehicles = [...new Set(fuelDetailRows.map((r) => r.vehicle).filter(Boolean))].sort((a, b) => String(a).localeCompare(String(b), 'es'));
        const sensors = [...new Set(fuelDetailRows.map((r) => r.sensor_id).filter(Boolean))].sort((a, b) => String(a).localeCompare(String(b), 'es'));
        const axles = [...new Set(fuelDetailRows.map((r) => r.axle).filter((v) => v !== null && v !== undefined))].sort((a, b) => Number(a) - Number(b));
        const positions = [...new Set(fuelDetailRows.map((r) => r.position).filter((v) => v !== null && v !== undefined))].sort((a, b) => Number(a) - Number(b));
        const profiles = [...new Set(fuelDetailRows.map((r) => r.alarm_profile).filter(Boolean))].sort((a, b) => String(a).localeCompare(String(b), 'es'));
        const severities = [...new Set(fuelDetailRows.map((r) => r.severity).filter(Boolean))].sort((a, b) => String(a).localeCompare(String(b), 'es'));
        return { companies, vehicles, sensors, axles, positions, profiles, severities };
    }, [fuelDetailRows]);

    const filteredFuelDetailRows = useMemo(() => {
        const now = dayjs();
        return fuelDetailRows.filter((row) => {
            if (dieselViewFilters.company && row.company !== dieselViewFilters.company) return false;
            if (dieselViewFilters.vehicle && row.vehicle !== dieselViewFilters.vehicle) return false;
            if (dieselViewFilters.sensor && row.sensor_id !== dieselViewFilters.sensor) return false;
            if (dieselViewFilters.axle !== '' && String(row.axle) !== String(dieselViewFilters.axle)) return false;
            if (dieselViewFilters.position !== '' && String(row.position) !== String(dieselViewFilters.position)) return false;
            if (dieselViewFilters.alarm_profile && row.alarm_profile !== dieselViewFilters.alarm_profile) return false;
            if (dieselViewFilters.severity && row.severity !== dieselViewFilters.severity) return false;

            if (dieselViewFilters.state_mode === 'current') {
                const ts = dayjs(row.timestamp);
                if (!ts.isValid()) return false;
                if (now.diff(ts, 'hour', true) > 24) return false;
            }

            return true;
        });
    }, [fuelDetailRows, dieselViewFilters]);

    const filteredFuelRows = useMemo(() => {
        if (!filteredFuelDetailRows.length) return fuelRows;
        const selectedVehicles = new Set(filteredFuelDetailRows.map((r) => r.vehicle));
        return fuelRows.filter((r) => selectedVehicles.has(r.vehicle));
    }, [fuelRows, filteredFuelDetailRows]);

    const fuelExecutive = useMemo(() => {
        const totalLiters = filteredFuelRows.reduce((acc, row) => acc + safeNumber(row.liters_extra_period, 0), 0);
        const totalCost = filteredFuelRows.reduce((acc, row) => acc + safeNumber(row.cost_extra_period, 0), 0);
        const avgPenalty = filteredFuelRows.length
            ? filteredFuelRows.reduce((acc, row) => acc + safeNumber(row.fuel_extra_pct_period, 0), 0) / filteredFuelRows.length
            : 0;
        const avgDeficit = filteredFuelRows.length
            ? filteredFuelRows.reduce((acc, row) => acc + safeNumber(row.psi_deficit_avg, 0), 0) / filteredFuelRows.length
            : 0;
        return { totalLiters, totalCost, avgPenalty, avgDeficit };
    }, [filteredFuelRows]);

    const chartPoints = useMemo(() => {
        // Convert each PSI field to the user's unit, but preserve NaN/null when
        // the backend didn't emit the field. Plotting a phantom 0-PSI line for
        // a missing series distorts the y-axis and confuses operators.
        const convertOrNaN = (raw) => {
            const n = Number(raw);
            if (!Number.isFinite(n)) return NaN;
            const c = convertPressureFromPSI(n, pressureUnit);
            return c === null ? NaN : safeNumber(c, NaN);
        };
        const points = (fuelChart?.points || []).map((p) => ({
            ...p,
            pressure_real_chart: convertOrNaN(p?.pressure_real),
            ideal_variable_chart: convertOrNaN(p?.ideal_variable),
            ideal_profile_chart: convertOrNaN(p?.ideal_profile),
            cold_current_chart: convertOrNaN(p?.cold_pressure_current),
            cold_start_chart: convertOrNaN(p?.cold_pressure_start),
            deficit_chart: convertOrNaN(p?.deficit),
        }));
        return points;
    }, [fuelChart, pressureUnit]);

    const markersMax = useMemo(() => {
        return (fuelChart?.maxima || []).map((m) => ({
            ...m,
            pressure_real_chart: convertPressureFromPSI(safeNumber(m.pressure_real, 0), pressureUnit),
        }));
    }, [fuelChart, pressureUnit]);

    const markersMin = useMemo(() => {
        return (fuelChart?.minima || []).map((m) => ({
            ...m,
            pressure_real_chart: convertPressureFromPSI(safeNumber(m.pressure_real, 0), pressureUnit),
        }));
    }, [fuelChart, pressureUnit]);

    const markersDeficit = useMemo(() => {
        return (fuelChart?.deficit_markers || []).map((m) => ({
            ...m,
            from_chart: convertPressureFromPSI(safeNumber(m.from, 0), pressureUnit),
            to_chart: convertPressureFromPSI(safeNumber(m.to, 0), pressureUnit),
        }));
    }, [fuelChart, pressureUnit]);

    const executiveChartOptions = useMemo(() => ({
        chart: { type: 'line', backgroundColor: 'transparent', height: 340 },
        title: { text: null },
        credits: { enabled: false },
        xAxis: {
            categories: (trendData || []).map((r) => r.date),
            crosshair: true,
        },
        yAxis: [
            { title: { text: t('hardcoded.pages_tpmsreportspage.eventos') } },
            { title: { text: t('hardcoded.pages_tpmsreportspage.sobrecosto_combustible') }, opposite: true },
        ],
        tooltip: { shared: true },
        legend: { enabled: true },
        series: [
            { name: t('hardcoded.pages_billingplanspage.alarmas'), type: 'line', data: (trendData || []).map((r) => safeNumber(r.alarms, 0)), color: '#1f77b4' },
            { name: t('hardcoded.pages_tpmsreportspage.criticas'), type: 'line', data: (trendData || []).map((r) => safeNumber(r.critical, 0)), color: '#d62728' },
            { name: t('hardcoded.pages_tpmsreportspage.sobrecosto_combustible'), type: 'line', yAxis: 1, data: (trendData || []).map((r) => safeNumber(r.fuel_extra_cost, 0)), color: '#2ca02c' },
        ],
    }), [trendData]);


    const temperatureStressOptions = useMemo(() => ({
        chart: { type: 'column', backgroundColor: 'transparent', height: 320 },
        title: { text: null },
        credits: { enabled: false },
        xAxis: { categories: (temperatureStress || []).map((r) => r.vehicle || '-') },
        yAxis: { title: { text: t('hardcoded.pages_tpmsreportspage.temperatura_temperaturesymbol', { temperatureSymbol: temperatureSymbol }) } },
        tooltip: {
            shared: true,
            pointFormatter: function pointFormatter() {
                return t('hardcoded.pages_tpmsreportspage.span_stylecolorcolorspan_name_bsafenumberthisy_temperaturesymbolbbr', { color: this.color, name: this.series.name, safeNumberthisy: safeNumber(this.y).toFixed(temperatureDecimals), temperatureSymbol: temperatureSymbol });
            },
        },
        series: [
            { name: t('hardcoded.pages_tpmreports.temp_max'), type: 'column', data: (temperatureStress || []).map((r) => safeNumber(convertTemperatureFromC(r.max_temp, temperatureUnit), 0)), color: '#d62728' },
            { name: t('hardcoded.pages_tpmreports.temp_promedio'), type: 'column', data: (temperatureStress || []).map((r) => safeNumber(convertTemperatureFromC(r.avg_temp, temperatureUnit), 0)), color: '#ff7f0e' },
        ],
    }), [temperatureStress, temperatureSymbol, temperatureDecimals, temperatureUnit]);

    const fuelTrendOptions = useMemo(() => ({
        chart: { type: 'line', backgroundColor: 'transparent', height: 350 },
        title: { text: null },
        credits: { enabled: false },
        xAxis: { categories: (fuelTrendRows || []).map((r) => r.date) },
        yAxis: [{ title: { text: t('hardcoded.pages_tpmsreportspage.litros_costo_penalizacion') } }],
        tooltip: {
            shared: true,
            pointFormatter: function pointFormatter() {
                if (this.series.name.includes('%')) {
                    return `<span style="color:${this.color}">●</span> ${this.series.name}: <b>${safeNumber(this.y).toFixed(2)}%</b><br/>`;
                }
                if (this.series.name.includes(t('hardcoded.pages_tpmsreportspage.costo'))) {
                    return t('hardcoded.pages_tpmsreportspage.span_stylecolorcolorspan_name_bformatcurrencytbbr', { color: this.color, name: this.series.name, formatCurrencyt: formatCurrency(this.y, reportCurrency) });
                }
                return t('hardcoded.pages_tpmsreportspage.span_stylecolorcolorspan_name_bsafenumberthisybbr', { color: this.color, name: this.series.name, safeNumberthisy: safeNumber(this.y).toFixed(2) });
            },
        },
        series: [
            { name: t('hardcoded.pages_tpmreports.col_litros_extra'), type: 'line', data: (fuelTrendRows || []).map((r) => safeNumber(r.liters_extra, 0)), color: '#FB8C00' },
            { name: t('hardcoded.pages_tpmsreportspage.costo_extra'), type: 'line', data: (fuelTrendRows || []).map((r) => safeNumber(r.cost_extra, 0)), color: '#D84315' },
            { name: t('hardcoded.pages_tpmsreportspage.penalizacion'), type: 'line', data: (fuelTrendRows || []).map((r) => safeNumber(r.avg_penalty_pct, 0)), color: '#6D4C41' },
        ],
    }), [fuelTrendRows, reportCurrency]);

    const abcSeriesByPosition = useMemo(() => {
        const vehicleName = dieselViewFilters.vehicle || fuelChart?.vehicle;
        if (!vehicleName) return [];

        const source = filteredFuelDetailRows
            .filter((r) => r.vehicle === vehicleName && r.timestamp)
            .sort((a, b) => dayjs(a.timestamp).valueOf() - dayjs(b.timestamp).valueOf());

        const grouped = source.reduce((acc, row) => {
            const key = t('hardcoded.components_schematics_vehicletelemetrymodal copy.pos_position', { position: row.position });
            if (!acc[key]) acc[key] = [];
            const x = toMillis(row.timestamp);
            const y = convertPressureFromPSI(safeNumber(row.pressure_real_psi, NaN), pressureUnit);
            if (x !== null && Number.isFinite(y)) {
                acc[key].push([x, safeNumber(y, 0)]);
            }
            return acc;
        }, {});

        return Object.entries(grouped)
            .sort((a, b) => a[0].localeCompare(b[0], 'es'))
            .slice(0, 12)
            .map(([name, data], idx) => ({
                name,
                type: 'line',
                data,
                lineWidth: 1.6,
                marker: { enabled: false },
                color: ['#5E35B1', '#00897B', '#3949AB', '#6D4C41', '#039BE5', '#7CB342', '#F4511E', '#546E7A'][idx % 8],
                tooltip: { valueSuffix: ` ${pressureSymbol}` },
            }));
    }, [filteredFuelDetailRows, dieselViewFilters.vehicle, fuelChart, pressureUnit, pressureSymbol]);

    const fuelCycleOptions = useMemo(() => {
        const mainSeries = {
            name: t('hardcoded.pages_tpmsreportspage.presion_sensor_seleccionado'),
            type: 'line',
            data: chartPoints
                .map((p) => [toMillis(p.timestamp), safeNumber(p.pressure_real_chart, NaN)])
                .filter((p) => p[0] !== null && Number.isFinite(p[1])),
            color: '#263238',
            lineWidth: 2.6,
            marker: { enabled: false },
            tooltip: { valueSuffix: ` ${pressureSymbol}` },
            zIndex: 4,
        };

        const idealSeries = {
            name: t('hardcoded.pages_tpmsreportspage.psi_ajustado_por_temp_info'),
            type: 'line',
            data: chartPoints
                .map((p) => [toMillis(p.timestamp), safeNumber(p.ideal_variable_chart, NaN)])
                .filter((p) => p[0] !== null && Number.isFinite(p[1])),
            color: 'rgba(30, 136, 229, 0.35)',
            dashStyle: 'ShortDot',
            lineWidth: 1,
            marker: { enabled: false },
            tooltip: { valueSuffix: ` ${pressureSymbol}` },
            zIndex: 2,
        };

        const idealProfileSeries = {
            name: t('hardcoded.pages_tpmsreportspage.psi_ideal_perfil'),
            type: 'line',
            data: chartPoints
                .map((p) => [toMillis(p.timestamp), safeNumber(p.ideal_profile_chart, NaN)])
                .filter((p) => p[0] !== null && Number.isFinite(p[1])),
            color: '#1B5E20',
            dashStyle: t('hardcoded.components_schematics_vehicletelemetrymodal.dash'),
            lineWidth: 1.4,
            marker: { enabled: false },
            tooltip: { valueSuffix: ` ${pressureSymbol}` },
            zIndex: 3,
        };

        const coldCurrentSeries = {
            name: t('hardcoded.pages_tpmsreportspage.presion_frio_estimada'),
            type: 'line',
            data: chartPoints
                .map((p) => [toMillis(p.timestamp), safeNumber(p.cold_current_chart, NaN)])
                .filter((p) => p[0] !== null && Number.isFinite(p[1])),
            color: '#1565C0',
            lineWidth: 2,
            marker: { enabled: false },
            tooltip: { valueSuffix: ` ${pressureSymbol}` },
            zIndex: 3,
        };

        const deficitSeries = {
            name: t('hardcoded.pages_tpmsreportspage.deficit_psi'),
            type: 'line',
            data: chartPoints
                .map((p) => [toMillis(p.timestamp), safeNumber(p.deficit_chart, NaN)])
                .filter((p) => p[0] !== null && Number.isFinite(p[1])),
            color: '#F57C00',
            dashStyle: t('hardcoded.components_schematics_vehicletelemetrymodal.dash'),
            lineWidth: 1.4,
            marker: { enabled: false },
            visible: true,
            zIndex: 1,
        };

        const penaltySeries = {
            name: t('hardcoded.pages_tpmsreportspage.penalizacion'),
            type: 'line',
            yAxis: 1,
            data: chartPoints
                .map((p) => [toMillis(p.timestamp), safeNumber(p.penalty_pct, NaN)])
                .filter((p) => p[0] !== null && Number.isFinite(p[1])),
            color: '#D32F2F',
            dashStyle: t('hardcoded.pages_alarmticketdetailpage.shortdash'),
            lineWidth: 1.2,
            marker: { enabled: false },
            tooltip: { valueSuffix: ' %' },
            zIndex: 1,
        };

        const maximaSeries = {
            name: t('hardcoded.pages_tpmreports.maximos_a'),
            type: 'scatter',
            data: markersMax
                .map((m) => ({ x: toMillis(m.timestamp), y: safeNumber(m.pressure_real_chart, NaN), label: m.label }))
                .filter((m) => m.x !== null && Number.isFinite(m.y)),
            color: '#00897B',
            marker: { radius: 4, symbol: 'circle' },
            dataLabels: {
                enabled: true,
                formatter: function formatter() { return this.point.label || ''; },
                style: { color: '#00695C', fontWeight: '700', textOutline: 'none' },
            },
            zIndex: 6,
        };

        const minimaSeries = {
            name: t('hardcoded.pages_tpmreports.minimos_b'),
            type: 'scatter',
            data: markersMin
                .map((m) => ({ x: toMillis(m.timestamp), y: safeNumber(m.pressure_real_chart, NaN), label: m.label }))
                .filter((m) => m.x !== null && Number.isFinite(m.y)),
            color: '#C62828',
            marker: { radius: 4, symbol: 'diamond' },
            dataLabels: {
                enabled: true,
                formatter: function formatter() { return this.point.label || ''; },
                style: { color: '#B71C1C', fontWeight: '700', textOutline: 'none' },
            },
            zIndex: 6,
        };

        const cSegments = markersDeficit.map((m) => ({
            name: m.label,
            type: 'line',
            data: (() => {
                const x = toMillis(m.timestamp);
                if (x === null) return [];
                return [
                    { x, y: safeNumber(m.from_chart, 0) },
                    { x, y: safeNumber(m.to_chart, 0), customLabel: m.label },
                ];
            })(),
            color: '#EF6C00',
            lineWidth: 1.6,
            dashStyle: t('hardcoded.pages_alarmticketdetailpage.shortdash'),
            marker: { enabled: false },
            enableMouseTracking: false,
            showInLegend: false,
            dataLabels: {
                enabled: true,
                formatter: function formatter() { return this.point.customLabel || null; },
                style: { color: '#EF6C00', fontWeight: '700', textOutline: 'none' },
            },
            zIndex: 5,
        }));

        return {
            chart: {
                zoomType: 'x',
                backgroundColor: 'transparent',
                height: 350,
            },
            title: { text: null },
            credits: { enabled: false },
            xAxis: {
                type: 'datetime',
                title: { text: t('hardcoded.components_alarmprofiles_profileform.tiempo') },
                labels: {
                    formatter: function formatter() {
                        return dayjs(this.value).format('DD/MM HH:mm');
                    },
                },
            },
            yAxis: [
                {
                    title: { text: `Presion (${pressureSymbol})` },
                    opposite: false,
                },
                {
                    title: { text: t('hardcoded.pages_tpmsreportspage.penalizacion') },
                    opposite: true,
                },
            ],
            tooltip: {
                shared: true,
                useHTML: true,
                xDateFormat: '%d/%m %H:%M',
            },
            legend: {
                enabled: true,
                align: 'center',
                verticalAlign: 'bottom',
            },
            series: [
                ...abcSeriesByPosition,
                idealProfileSeries,
                coldCurrentSeries,
                idealSeries,
                mainSeries,
                deficitSeries,
                penaltySeries,
                maximaSeries,
                minimaSeries,
                ...cSegments,
            ],
        };
    }, [chartPoints, markersMax, markersMin, markersDeficit, pressureSymbol, abcSeriesByPosition]);

    return (
        <Box sx={{ pb: 4 }}>
            <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'center' }} spacing={2} sx={{ mb: 2 }}>
                <Box>
                    <Typography variant="h4" fontWeight={700} color="primary.main">{i18n.t('hardcoded.pages_tpmsreportspage.reportes_tpms_-_performance_y')}</Typography>
                    <Typography variant="body1" color="text.secondary">
                        {t('hardcoded.pages_tpmsreportspage.analitica_operativa_y_economica_para')}</Typography>
                </Box>

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                    <Button variant="outlined" startIcon={<RefreshIcon />} onClick={loadReport} disabled={loading}>{i18n.t('audit.actions.UPDATE')}</Button>
                    <Button variant="outlined" startIcon={<PrintIcon />} onClick={() => window.print()}>{t('pages_tpmsreportspage.button_print')}</Button>
                    <Button variant="contained" startIcon={<DownloadIcon />} onClick={exportCurrentTab} disabled={exporting}>
                        {i18n.t('hardcoded.pages_tpmsreportspage.exportar_csv_oficial')}</Button>
                    <AskAIButton prompt={t('hardcoded.pages_tpmsreportspage.interpreta_mi_reporte_tpms_dame')} label={t('hardcoded.pages_tpmsreportspage.interpretar_con_ia')} />
                </Stack>
            </Stack>

            <AIRecommendationsPanel module="reports" />

            <Paper sx={{ p: 2, mb: 2 }}>
                <Grid container spacing={2}>
                    {/* ============ SECCIÓN 1: PERIODO Y ALCANCE ============ */}
                    <Grid item xs={12}>
                        <Typography variant="overline" sx={{ color: 'primary.main', fontWeight: 700, letterSpacing: 0.5 }}>
                            {i18n.t('hardcoded.pages_tpmsreportspage.1_periodo_y_alcance')}</Typography>
                        <Divider sx={{ mt: 0.5, mb: 1 }} />
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                        <TextField
                            type="date"
                            size="small"
                            fullWidth
                            label={t('audit.filters.from')}
                            InputLabelProps={{ shrink: true }}
                            value={filters.start_date}
                            onChange={(event) => handleFilterChange('start_date', event.target.value)}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                        <TextField
                            type="date"
                            size="small"
                            fullWidth
                            label={t('audit.filters.to')}
                            InputLabelProps={{ shrink: true }}
                            value={filters.end_date}
                            onChange={(event) => handleFilterChange('end_date', event.target.value)}
                        />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <FormControl fullWidth size="small">
                            <InputLabel shrink>{t('vehicles.title')}</InputLabel>
                            <Select
                                multiple
                                displayEmpty
                                notched
                                value={filters.vehicle_ids}
                                label={t('vehicles.title')}
                                onChange={(event) => handleFilterChange('vehicle_ids', event.target.value)}
                                renderValue={(selected) => selected.length ? t('hardcoded.pages_tpmsreportspage.length_seleccionados', { length: selected.length }) : t('hardcoded.pages_tpmreports.todos')}
                            >
                                {vehicles.map((vehicle) => (
                                    <MenuItem key={vehicle.id} value={vehicle.id}>{vehicle.display_name}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                        <FormControl fullWidth size="small">
                            <InputLabel shrink>{i18n.t('hardcoded.pages_tpmsreportspage.estado_ticket')}</InputLabel>
                            <Select
                                value={filters.ticket_status}
                                displayEmpty
                                label={t('hardcoded.pages_tpmsreportspage.estado_ticket')}
                                notched
                                onChange={(event) => handleFilterChange('ticket_status', event.target.value)}
                                renderValue={(v) => v === 'open' ? t('hardcoded.pages_tpmreports.abierto') : v === 'acknowledged' ? t('hardcoded.pages_tpmreports.visto') : v === 'resolved' ? t('hardcoded.pages_tpmreports.col_resuelto') : t('hardcoded.pages_tpmreports.todos')}
                            >
                                <MenuItem value="">{i18n.t('hardcoded.components_alarmprofiles_assignmentstable.todos')}</MenuItem>
                                <MenuItem value="open">{i18n.t('alarmTickets.status.open')}</MenuItem>
                                <MenuItem value="acknowledged">{i18n.t('alarmTickets.status.acknowledged')}</MenuItem>
                                <MenuItem value="resolved">{i18n.t('alarmTickets.status.resolved')}</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <FormControl fullWidth size="small">
                            <InputLabel shrink>{i18n.t('hardcoded.pages_tpmsreportspage.tipo_alerta')}</InputLabel>
                            <Select
                                value={filters.metric_type}
                                displayEmpty
                                notched
                                label={t('hardcoded.pages_tpmsreportspage.tipo_alerta')}
                                onChange={(event) => handleFilterChange('metric_type', event.target.value)}
                                renderValue={(v) => v === 'PSI' ? t('hardcoded.pages_tpmsreportspage.presion_pressuresymbol', { pressureSymbol: pressureSymbol }) : v === 'TEMP' ? t('hardcoded.pages_tpmreports.temperatura') : t('hardcoded.pages_tpmsreportspage.presion_temperatura')}
                            >
                                <MenuItem value="">{t('hardcoded.pages_tpmsreportspage.presion_temperatura')}</MenuItem>
                                <MenuItem value="PSI">{t('hardcoded.components_schematics_vehicletelemetrymodal_tabletab.presion')}{pressureSymbol})</MenuItem>
                                <MenuItem value="TEMP">{i18n.t('hardcoded.components_alarmprofiles_profilecard.temperatura')}</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <TextField
                            label={t('hardcoded.pages_tpmsreportspage.km_por_vehiculo_en_el')}
                            size="small"
                            type="number"
                            fullWidth
                            value={filters.estimated_km}
                            onChange={(event) => handleFilterChange('estimated_km', event.target.value)}
                            helperText={t('hardcoded.pages_tpmsreportspage.opcional_aplica_a_cada_vehiculo')}
                            InputLabelProps={{ shrink: true }}
                        />
                    </Grid>

                    {/* ============ SECCIÓN 2: PARÁMETROS ECONÓMICOS ============ */}
                    <Grid item xs={12} sx={{ mt: 1 }}>
                        <Typography variant="overline" sx={{ color: 'primary.main', fontWeight: 700, letterSpacing: 0.5 }}>
                            {t('hardcoded.pages_tpmsreportspage.2_parametros_economicos')}</Typography>
                        <Divider sx={{ mt: 0.5, mb: 1 }} />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <FormControl fullWidth size="small">
                            <InputLabel shrink>{i18n.t('settings.company.currency')}</InputLabel>
                            <Select
                                value={filters.currency}
                                displayEmpty
                                notched
                                label={t('settings.company.currency')}
                                onChange={(event) => handleFilterChange('currency', event.target.value)}
                                renderValue={(v) => {
                                    const cur = REPORT_CURRENCIES.find((c) => c.code === v);
                                    return cur ? `${cur.code} (${cur.symbol})` : (v || 'MXN');
                                }}
                            >
                                {REPORT_CURRENCIES.map((c) => (
                                    <MenuItem key={c.code} value={c.code}>{`${c.code} · ${c.symbol} · ${c.name}`}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <TextField
                            label={t('hardcoded.pages_tpmsreportspage.precio_combustible_reportcurrency_l', { reportCurrency: reportCurrency })}
                            size="small"
                            type="number"
                            fullWidth
                            value={filters.diesel_price}
                            onChange={(event) => handleFilterChange('diesel_price', event.target.value)}
                            InputProps={{ endAdornment: <InputAdornment position="end">{reportCurrency}</InputAdornment> }}
                            InputLabelProps={{ shrink: true }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <TextField
                            label={t('hardcoded.pages_tpmsreportspage.costo_llanta_reportcurrency', { reportCurrency: reportCurrency })}
                            size="small"
                            type="number"
                            fullWidth
                            value={filters.tire_replacement_cost}
                            onChange={(event) => handleFilterChange('tire_replacement_cost', event.target.value)}
                            InputProps={{ endAdornment: <InputAdornment position="end">{reportCurrency}</InputAdornment> }}
                            InputLabelProps={{ shrink: true }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <TextField
                            label={t('hardcoded.pages_tpmsreportspage.km_totales_por_llanta')}
                            size="small"
                            type="number"
                            fullWidth
                            value={filters.tire_total_km}
                            onChange={(event) => handleFilterChange('tire_total_km', event.target.value)}
                            helperText={t('hardcoded.pages_tpmsreportspage.km_promedio_que_dura_una')}
                            InputProps={{ endAdornment: <InputAdornment position="end">km</InputAdornment> }}
                            InputLabelProps={{ shrink: true }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <TextField
                                label={t('hardcoded.pages_tpmsreportspage.rendimiento')}
                                size="small"
                                type="number"
                                fullWidth
                                value={filters.consumption_value}
                                onChange={(event) => handleFilterChange('consumption_value', event.target.value)}
                                InputLabelProps={{ shrink: true }}
                            />
                            <FormControl size="small" sx={{ minWidth: 110 }}>
                                <InputLabel shrink>{t('hardcoded.components_alarmprofiles_profileform.unidad')}</InputLabel>
                                <Select
                                    value={filters.consumption_unit}
                                    displayEmpty
                                    notched
                                    label={t('hardcoded.components_alarmprofiles_profileform.unidad')}
                                    onChange={(event) => handleFilterChange('consumption_unit', event.target.value)}
                                    renderValue={(v) => v || 'L/100km'}
                                >
                                    {CONSUMPTION_UNITS.map((u) => (
                                        <MenuItem key={u} value={u}>{u}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>
                    </Grid>

                    {/* Panel de tasas de cambio */}
                    <Grid item xs={12}>
                        <Paper variant="outlined" sx={{ p: 1.5, borderColor: showRatesPanel ? 'primary.main' : 'divider' }}>
                            <Stack direction="row" justifyContent="space-between" alignItems="center">
                                <Box>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                        {t('hardcoded.pages_tpmsreportspage.tipo_de_cambio')}</Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {t('hardcoded.pages_tpmsreportspage.necesario_para_mostrar_montos_en')}</Typography>
                                </Box>
                                <Button size="small" variant={showRatesPanel ? 'outlined' : 'text'} onClick={() => setShowRatesPanel((p) => !p)}>
                                    {showRatesPanel ? t('common.close') : t('hardcoded.pages_tpmsreportspage.configurar_tipo_de_cambio')}
                                </Button>
                            </Stack>
                            {showRatesPanel && (
                                <Box sx={{ mt: 1.5 }}>
                                    <Grid container spacing={1}>
                                        {REPORT_CURRENCIES.filter((c) => c.code !== 'MXN').map((c) => (
                                            <Grid item xs={6} sm={4} md={3} key={c.code}>
                                                <TextField
                                                    size="small"
                                                    label={`1 ${c.code} = ? MXN`}
                                                    value={currencyRatesDisplay[c.code] || ''}
                                                    onChange={(e) => setCurrencyRatesDisplay((prev) => ({ ...prev, [c.code]: e.target.value }))}
                                                    type="number"
                                                    fullWidth
                                                    placeholder="ej: 17.5"
                                                    inputProps={{ min: 0, step: 0.0001 }}
                                                />
                                            </Grid>
                                        ))}
                                    </Grid>
                                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1.5 }}>
                                        <Button variant="contained" size="small" color="primary" onClick={saveCurrencyRates}>
                                            {t('hardcoded.pages_tpmsreportspage.guardar_tasas')}</Button>
                                        <Typography variant="caption" color="text.secondary">
                                            {t('hardcoded.pages_tpmsreportspage.las_tasas_se_guardan_en')}</Typography>
                                    </Stack>
                                </Box>
                            )}
                        </Paper>
                    </Grid>

                    {/* ============ SECCIÓN 3: HORARIO OPERATIVO ============ */}
                    <Grid item xs={12} sx={{ mt: 1 }}>
                        <Typography variant="overline" sx={{ color: 'primary.main', fontWeight: 700, letterSpacing: 0.5 }}>
                            {i18n.t('hardcoded.pages_tpmsreportspage.3_horario_operativo')}</Typography>
                        <Divider sx={{ mt: 0.5, mb: 1 }} />
                    </Grid>
                    <Grid item xs={12} md={8}>
                        <Paper variant="outlined" sx={{ p: 1.5 }}>
                            <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
                                {t('hardcoded.pages_tpmsreportspage.dias_y_turnos')}</Typography>

                            {/* Selector de días */}
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                                {t('hardcoded.pages_tpmsreportspage.dias_laborales')}</Typography>
                            <Stack direction="row" spacing={0.5} sx={{ mb: 1.5, flexWrap: 'wrap', gap: 0.5 }}>
                                {WORKDAYS.map((d) => {
                                    const active = (operationalConfig.workdays || []).includes(d.key);
                                    return (
                                        <Chip
                                            key={d.key}
                                            label={d.label}
                                            size="small"
                                            color={active ? 'primary' : 'default'}
                                            variant={active ? 'filled' : 'outlined'}
                                            onClick={() => {
                                                setOperationalConfig((prev) => {
                                                    const set = new Set(prev.workdays || []);
                                                    if (set.has(d.key)) set.delete(d.key);
                                                    else set.add(d.key);
                                                    return { ...prev, workdays: [...set].sort((a, b) => a - b) };
                                                });
                                            }}
                                            sx={{ cursor: 'pointer' }}
                                        />
                                    );
                                })}
                            </Stack>

                            {/* Modalidad de turno */}
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                                {t('hardcoded.pages_tpmsreportspage.modalidad_de_turno')}</Typography>
                            <ToggleButtonGroup
                                value={operationalConfig.shift_mode || 'single'}
                                exclusive
                                size="small"
                                onChange={(_, val) => val && setOperationalConfig((prev) => ({ ...prev, shift_mode: val }))}
                                sx={{ mb: 1.5 }}
                            >
                                <ToggleButton value="single">1 turno</ToggleButton>
                                <ToggleButton value="double">2 turnos</ToggleButton>
                                <ToggleButton value="24h">Continuo 24h</ToggleButton>
                            </ToggleButtonGroup>

                            {/* Horas por turno — oculto en modo 24h */}
                            {operationalConfig.shift_mode !== '24h' && (
                                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                                    <TextField
                                        size="small"
                                        type="number"
                                        label={t('hardcoded.pages_tpmsreportspage.horas_por_turno')}
                                        value={operationalConfig.hours_per_day}
                                        onChange={(e) => setOperationalConfig((prev) => ({ ...prev, hours_per_day: safeNumber(e.target.value, 8) }))}
                                        inputProps={{ min: 1, max: 24 }}
                                        sx={{ width: 170 }}
                                    />
                                    <Typography variant="caption" color="text.secondary">
                                        {operationalConfig.shift_mode === 'double'
                                            ? t('hardcoded.pages_tpmsreportspage.2_turnos_mathmin24safenu_hdia', { Mathmin24safeNu: Math.min(24, safeNumber(operationalConfig.hours_per_day, 8) * 2) })
                                            : t('hardcoded.pages_tpmsreportspage.hdia')}
                                    </Typography>
                                </Stack>
                            )}

                            {/* Resumen calculado */}
                            {(() => {
                                const days = (operationalConfig.workdays || []).length;
                                const hpd = operationalConfig.shift_mode === '24h'
                                    ? 24
                                    : operationalConfig.shift_mode === 'double'
                                        ? Math.min(24, safeNumber(operationalConfig.hours_per_day, 8) * 2)
                                        : safeNumber(operationalConfig.hours_per_day, 8);
                                return (
                                    <Typography variant="caption" sx={{ color: 'primary.main', fontWeight: 500 }}>
                                        {days} {t('hardcoded.pages_tpmsreportspage.dias')}{hpd} h = <strong>{days * hpd} h/semana</strong> {i18n.t('hardcoded.pages_tpmsreportspage.operativas')}</Typography>
                                );
                            })()}
                        </Paper>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                            <Button variant="contained" fullWidth onClick={handleApplyFilters}>{i18n.t('hardcoded.pages_tpmsreportspage.aplicar_filtros')}</Button>
                            <Button variant="outlined" fullWidth onClick={handleResetFilters}>{i18n.t('common.clear')}</Button>
                        </Stack>
                    </Grid>
                </Grid>
            </Paper>

            {(loading || exporting) && <LinearProgress sx={{ mb: 2 }} />}

            {!report && !loading && (
                <Alert severity="warning" icon={<WarningIcon />} sx={{ mb: 2 }}>
                    {t('hardcoded.pages_tpmsreportspage.no_se_encontraron_datos_de')}</Alert>
            )}

            {!!currencyWarning && (
                <Alert
                    severity="warning"
                    sx={{ mb: 2 }}
                    action={
                        <Button
                            color="inherit"
                            size="small"
                            onClick={() => {
                                setShowRatesPanel(true);
                                window.scrollTo({ top: 0, behavior: 'smooth' });
                            }}
                        >
                            {t('hardcoded.pages_tpmsreportspage.configurar_tasa')}</Button>
                    }
                >
                    {currencyWarning}
                </Alert>
            )}

            <KPIGrid items={executiveKpis} />

            <Paper sx={{ mb: 2 }}>
                <Tabs
                    value={tab}
                    onChange={(_, value) => setTab(value)}
                    variant="scrollable"
                    scrollButtons="auto"
                >
                    {REPORT_TABS.map((item) => (
                        <Tab key={item.key} value={item.key} label={item.label} />
                    ))}
                </Tabs>
            </Paper>

            {tab === 'executive' && (
                <Grid container spacing={2}>
                    <Grid item xs={12} lg={8}>
                        <Paper sx={{ p: 2, height: 420 }}>
                            <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>{t('hardcoded.pages_tpmsreportspage.tendencia_de_costo_y_eventos')}</Typography>
                            <HighchartsReact highcharts={Highcharts} options={executiveChartOptions} />
                        </Paper>
                    </Grid>
                    <Grid item xs={12} lg={4}>
                        <Paper sx={{ p: 2, height: 420 }}>
                            <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>{t('hardcoded.pages_tpmsreportspage.top_unidades_problematicas')}</Typography>
                            {(report?.executive_dashboard?.most_problematic_units || []).slice(0, 10).map((item) => (
                                <Box key={item.vehicle} sx={{ mb: 1.5 }}>
                                    <Stack direction="row" justifyContent="space-between">
                                        <Typography variant="body2">{item.vehicle}</Typography>
                                        <Typography variant="body2" fontWeight={700}>{item.events}</Typography>
                                    </Stack>
                                    <LinearProgress variant="determinate" value={Math.min(100, safeNumber(item.events) * 5)} sx={{ mt: 0.5 }} />
                                </Box>
                            ))}
                        </Paper>
                    </Grid>
                </Grid>
            )}

            {tab === 'fuel' && (
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <KPIGrid items={[
                            { label: t('hardcoded.pages_tpmreports.litros_extra_estimados'), value: formatNumber(fuelExecutive.totalLiters), color: 'warning.main' },
                            { label: t('hardcoded.pages_tpmreports.col_sobrecosto_combustible'), value: formatCurrency(fuelExecutive.totalCost, reportCurrency), color: 'error.main', helpKey: 'diesel_cost' },
                            { label: t('hardcoded.pages_tpmreports.deficit_promedio'), value: formatPressureDelta(fuelExecutive.avgDeficit), color: 'warning.main' },
                            { label: t('hardcoded.pages_tpmreports.penalizacion_promedio'), value: `${formatNumber(fuelExecutive.avgPenalty)}%`, color: 'info.main' },
                        ]} />
                    </Grid>

                    <Grid item xs={12}>
                        <Paper sx={{ p: 2 }}>
                            <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>{i18n.t('hardcoded.pages_tpmsreportspage.filtros_especificos_combustible')}</Typography>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6} md={3}>
                                    <FormControl fullWidth size="small">
                                        <InputLabel>{i18n.t('navbar.company')}</InputLabel>
                                        <Select
                                            value={dieselViewFilters.company}
                                            label={t('navbar.company')}
                                            onChange={(event) => setDieselViewFilters((prev) => ({ ...prev, company: event.target.value }))}
                                        >
                                            <MenuItem value="">{i18n.t('hardcoded.components_settings_notificationpreferenceseditor.todas')}</MenuItem>
                                            {dieselFilterOptions.companies.map((item) => (
                                                <MenuItem key={item} value={item}>{item}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} sm={6} md={3}>
                                    <FormControl fullWidth size="small">
                                        <InputLabel>{i18n.t('hardcoded.pages_tpmsreportspage.vehiculo')}</InputLabel>
                                        <Select
                                            value={dieselViewFilters.vehicle}
                                            label={t('hardcoded.pages_tpmsreportspage.vehiculo')}
                                            onChange={(event) => setDieselViewFilters((prev) => ({ ...prev, vehicle: event.target.value }))}
                                        >
                                            <MenuItem value="">{i18n.t('hardcoded.components_alarmprofiles_assignmentstable.todos')}</MenuItem>
                                            {dieselFilterOptions.vehicles.map((item) => (
                                                <MenuItem key={item} value={item}>{item}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} sm={6} md={2}>
                                    <FormControl fullWidth size="small">
                                        <InputLabel>{i18n.t('hardcoded.components_schematics_vehicleticketsdrawer.sensor')}</InputLabel>
                                        <Select
                                            value={dieselViewFilters.sensor}
                                            label={t('hardcoded.components_schematics_vehicleticketsdrawer.sensor')}
                                            onChange={(event) => setDieselViewFilters((prev) => ({ ...prev, sensor: event.target.value }))}
                                        >
                                            <MenuItem value="">{i18n.t('hardcoded.components_alarmprofiles_assignmentstable.todos')}</MenuItem>
                                            {dieselFilterOptions.sensors.map((item) => (
                                                <MenuItem key={item} value={item}>{item}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} sm={6} md={2}>
                                    <FormControl fullWidth size="small">
                                        <InputLabel>{i18n.t('vehicleDetail.alarms.axle')}</InputLabel>
                                        <Select
                                            value={dieselViewFilters.axle}
                                            label={t('vehicleDetail.alarms.axle')}
                                            onChange={(event) => setDieselViewFilters((prev) => ({ ...prev, axle: event.target.value }))}
                                        >
                                            <MenuItem value="">{i18n.t('hardcoded.components_alarmprofiles_assignmentstable.todos')}</MenuItem>
                                            {dieselFilterOptions.axles.map((item) => (
                                                <MenuItem key={item} value={item}>{i18n.t('vehicleDetail.alarms.axle')}{item}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} sm={6} md={2}>
                                    <FormControl fullWidth size="small">
                                        <InputLabel>{i18n.t('hardcoded.pages_tpmsreportspage.posicion')}</InputLabel>
                                        <Select
                                            value={dieselViewFilters.position}
                                            label={t('hardcoded.pages_tpmsreportspage.posicion')}
                                            onChange={(event) => setDieselViewFilters((prev) => ({ ...prev, position: event.target.value }))}
                                        >
                                            <MenuItem value="">{i18n.t('hardcoded.components_settings_notificationpreferenceseditor.todas')}</MenuItem>
                                            {dieselFilterOptions.positions.map((item) => (
                                                <MenuItem key={item} value={item}>{i18n.t('hardcoded.components_schematics_vehicletelemetrymodal.pos')}{item}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} sm={6} md={3}>
                                    <FormControl fullWidth size="small">
                                        <InputLabel>{t('hardcoded.pages_tpmsreportspage.perfil_alarma')}</InputLabel>
                                        <Select
                                            value={dieselViewFilters.alarm_profile}
                                            label={t('hardcoded.pages_tpmsreportspage.perfil_alarma')}
                                            onChange={(event) => setDieselViewFilters((prev) => ({ ...prev, alarm_profile: event.target.value }))}
                                        >
                                            <MenuItem value="">{i18n.t('hardcoded.components_alarmprofiles_assignmentstable.todos')}</MenuItem>
                                            {dieselFilterOptions.profiles.map((item) => (
                                                <MenuItem key={item} value={item}>{item}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} sm={6} md={2}>
                                    <FormControl fullWidth size="small">
                                        <InputLabel>{t('hardcoded.components_settings_notificationpreferenceseditor.severidad')}</InputLabel>
                                        <Select
                                            value={dieselViewFilters.severity}
                                            label={t('hardcoded.components_settings_notificationpreferenceseditor.severidad')}
                                            onChange={(event) => setDieselViewFilters((prev) => ({ ...prev, severity: event.target.value }))}
                                        >
                                            <MenuItem value="">{i18n.t('hardcoded.components_settings_notificationpreferenceseditor.todas')}</MenuItem>
                                            {dieselFilterOptions.severities.map((item) => (
                                                <MenuItem key={item} value={item}>{item}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} sm={6} md={2}>
                                    <FormControl fullWidth size="small">
                                        <InputLabel>{i18n.t('gps.table.status')}</InputLabel>
                                        <Select
                                            value={dieselViewFilters.state_mode}
                                            label={t('gps.table.status')}
                                            onChange={(event) => setDieselViewFilters((prev) => ({ ...prev, state_mode: event.target.value }))}
                                        >
                                            <MenuItem value="all">{i18n.t('hardcoded.pages_tpmsreportspage.historico')}</MenuItem>
                                            <MenuItem value="current">{i18n.t('hardcoded.pages_tpmsreportspage.actual_24h')}</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>
                            </Grid>
                        </Paper>
                    </Grid>

                    <Grid item xs={12} lg={8}>
                        <Paper sx={{ p: 2, height: 440 }}>
                            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                                <Typography variant="h6" fontWeight={700}>{t('hardcoded.pages_tpmsreportspage.esquema_de_ciclo_de_presion')}</Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {fuelChart?.vehicle || '-'} {i18n.t('hardcoded.pages_tpmsreportspage.sensor')}{fuelChart?.sensor_id || '-'} | {fuelChart?.cycle_label || t('hardcoded.pages_tpmreports.n_a')}
                                </Typography>
                            </Stack>
                            <HighchartsReact highcharts={Highcharts} options={fuelCycleOptions} />
                            <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
                                {markersMax.map((m) => (
                                    <Typography key={`lbl-${m.label}`} variant="caption" color="text.secondary">
                                        {m.label}: {dayjs(m.timestamp).format('DD/MM HH:mm')}
                                    </Typography>
                                ))}
                                {markersMin.map((m) => (
                                    <Typography key={`lbl-${m.label}`} variant="caption" color="text.secondary">
                                        {m.label}: {dayjs(m.timestamp).format('DD/MM HH:mm')}
                                    </Typography>
                                ))}
                            </Stack>
                        </Paper>
                    </Grid>

                    <Grid item xs={12} lg={4}>
                        <Paper sx={{ p: 2, height: 440 }}>
                            <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>{i18n.t('hardcoded.pages_tpmsreportspage.tendencia_combustible')}</Typography>
                            <HighchartsReact highcharts={Highcharts} options={fuelTrendOptions} />
                        </Paper>
                    </Grid>

                    <Grid item xs={12}>
                        <DataTable
                            title={t('hardcoded.pages_tpmsreportspage.ranking_de_ahorro_y_sobrecosto')}
                            rows={filteredFuelRows}
                            columns={[
                                { key: 'vehicle', label: t('hardcoded.pages_tpmreports.vehiculo') },
                                { key: 'vehicle_type', label: t('hardcoded.pages_tpmreports.tipo') },
                                { key: 'company', label: t('hardcoded.pages_tpmreports.empresa') },
                                { key: 'psi_ideal_avg', label: t('hardcoded.pages_tpmsreportspage.psi_ideal_perfil_pressuresymbol', { pressureSymbol: pressureSymbol }), render: (v) => formatPressureFromStored(v), csv: (v) => csvPressureFromStored(v), help: COLUMN_HELP.psi_ideal_profile },
                                { key: 'cold_pressure_avg', label: t('hardcoded.pages_tpmsreportspage.presion_frio_estimada_pressuresymbol', { pressureSymbol: pressureSymbol }), render: (v) => formatPressureFromStored(v), csv: (v) => csvPressureFromStored(v), help: COLUMN_HELP.cold_pressure_avg },
                                { key: 'psi_real_avg', label: t('hardcoded.pages_tpmsreportspage.presion_real_pressuresymbol', { pressureSymbol: pressureSymbol }), render: (v) => formatPressureReal(v), csv: (v) => csvPressureReal(v) },
                                { key: 'psi_vs_ideal_avg', label: t('hardcoded.pages_tpmsreportspage.psi_vs_ideal_pressuresymbol', { pressureSymbol: pressureSymbol }), render: (v) => formatPressureDelta(v), csv: (v) => csvPressureDelta(v), help: COLUMN_HELP.psi_vs_ideal_avg },
                                { key: 'psi_deficit_avg', label: t('hardcoded.pages_tpmsreportspage.deficit_psi_pressuresymbol', { pressureSymbol: pressureSymbol }), render: (v) => formatPressureDelta(v), csv: (v) => csvPressureDelta(v), help: COLUMN_HELP.psi_deficit },
                                { key: 'fuel_extra_pct_period', label: t('hardcoded.pages_tpmreports.porcentaje_penalizacion'), render: (v) => `${formatNumber(v)}%`, csv: (v) => csvPercent(v) },
                                { key: 'liters_extra_period', label: t('hardcoded.pages_tpmreports.col_litros_extra'), render: (v) => formatNumber(v), csv: (v) => csvNumber(v) },
                                { key: 'cost_extra_period', label: t('hardcoded.pages_tpmreports.col_sobrecosto_combustible'), render: (v) => formatCurrency(v, reportCurrency), csv: (v) => csvCurrency(v) },
                                { key: 'tire_loss_cost', label: t('hardcoded.pages_tpmreports.costo_desgaste_llanta'), render: (v) => formatCurrency(v, reportCurrency), csv: (v) => csvCurrency(v) },
                                { key: 'potential_saving', label: t('hardcoded.pages_tpmreports.ahorro_potencial'), render: (v) => formatCurrency(v, reportCurrency), csv: (v) => csvCurrency(v) },
                                { key: 'inflation_events', label: t('hardcoded.pages_tpmreports.inflados_detectados'), render: (v) => formatNumber(v), csv: (v) => csvNumber(v), help: COLUMN_HELP.inflation_detected },
                                { key: 'low_pressure_hours', label: t('hardcoded.pages_tpmreports.horas_baja_presion') },
                                { key: 'severity', label: t('hardcoded.components_settings_notificationpreferenceseditor.severidad') },
                            ]}
                            defaultSort={{ key: 'potential_saving', order: 'desc' }}
                            onExportCsv={(rows, cols) => exportLocalRows(rows, 'diesel_ahorro', cols)}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <DataTable
                            tableKey="fuel_detail"
                            title={t('hardcoded.pages_tpmsreportspage.detalle_de_impacto_combustible_por')}
                            rows={filteredFuelDetailRows}
                            columns={[
                                { key: 'timestamp', label: t('hardcoded.pages_tpmreports.fecha_hora'), render: (v) => formatUserDateTime(v), csv: (v) => formatCsvDateTime(v) },
                                { key: 'vehicle', label: t('hardcoded.pages_tpmreports.vehiculo') },
                                { key: 'company', label: t('hardcoded.pages_tpmreports.empresa') },
                                { key: 'sensor_id', label: t('hardcoded.pages_tpmreports.sensor') },
                                { key: 'axle', label: t('hardcoded.pages_tpmreports.eje') },
                                { key: 'position', label: t('hardcoded.pages_tpmreports.posicion') },
                                { key: 'cycle_id', label: t('hardcoded.pages_tpmreports.ciclo') },
                                { key: 'pressure_real_psi', label: t('hardcoded.pages_tpmsreportspage.psi_real_pressuresymbol', { pressureSymbol: pressureSymbol }), render: (v) => formatPressureReal(v), csv: (v) => csvPressureReal(v) },
                                { key: 'temperature_c', label: t('hardcoded.pages_tpmsreportspage.temp_temperaturesymbol', { temperatureSymbol: temperatureSymbol }), render: (v) => formatTemperatureValue(v), csv: (v) => csvTemperatureValue(v) },
                                { key: 'psi_ideal_profile', label: t('hardcoded.pages_tpmsreportspage.psi_ideal_perfil_pressuresymbol', { pressureSymbol: pressureSymbol }), render: (v, row) => formatPressureFromStored(v ?? row?.psi_ideal_base), csv: (v, row) => csvPressureFromStored(v ?? row?.psi_ideal_base), help: COLUMN_HELP.psi_ideal_profile },
                                { key: 'cold_pressure_start', label: t('hardcoded.pages_tpmsreportspage.presion_frio_inicio_viaje_pressuresymbol', { pressureSymbol: pressureSymbol }), render: (v) => formatPressureFromStored(v), csv: (v) => csvPressureFromStored(v), help: COLUMN_HELP.cold_pressure_start },
                                { key: 'cold_pressure_current', label: t('hardcoded.pages_tpmsreportspage.presion_frio_estimada_pressuresymbol', { pressureSymbol: pressureSymbol }), render: (v) => formatPressureFromStored(v), csv: (v) => csvPressureFromStored(v), help: COLUMN_HELP.cold_pressure_current },
                                { key: 'psi_vs_ideal', label: t('hardcoded.pages_tpmsreportspage.psi_vs_ideal_pressuresymbol', { pressureSymbol: pressureSymbol }), render: (v) => formatPressureDelta(v), csv: (v) => csvPressureDelta(v), help: COLUMN_HELP.psi_vs_ideal },
                                { key: 'temp_pressure_rise', label: t('hardcoded.pages_tpmsreportspage.aumento_por_temp_pressuresymbol', { pressureSymbol: pressureSymbol }), render: (v) => formatPressureDelta(v), csv: (v) => csvPressureDelta(v), help: COLUMN_HELP.temp_pressure_rise },
                                { key: 'psi_ideal_variable', label: t('hardcoded.pages_tpmsreportspage.psi_ajustado_por_temp_pressuresymbol', { pressureSymbol: pressureSymbol }), render: (v) => formatPressureFromStored(v), csv: (v) => csvPressureFromStored(v), help: COLUMN_HELP.psi_ideal_variable },
                                { key: 'psi_deficit', label: t('hardcoded.pages_tpmsreportspage.deficit_psi_pressuresymbol', { pressureSymbol: pressureSymbol }), render: (v) => formatPressureDelta(v), csv: (v) => csvPressureDelta(v), help: COLUMN_HELP.psi_deficit },
                                {
                                    key: 'inflation_detected',
                                    label: t('hardcoded.pages_tpmreports.inflado_detectado'),
                                    help: COLUMN_HELP.inflation_detected,
                                    render: (v) => (v ? <Chip size="small" color="warning" label={t('hardcoded.pages_tpmsreportspage.inflado')} /> : '-'),
                                    // CSV uses Si/No so pivot tables can count both sides
                                    // (empty string would be indistinguishable from missing data).
                                    csv: (v) => (v ? t('hardcoded.pages_tpmreports.si') : t('common.no')),
                                },
                                { key: 'psi_inflated_estimated', label: t('hardcoded.pages_tpmsreportspage.psi_inflado_estimado_pressuresymbol', { pressureSymbol: pressureSymbol }), render: (v) => formatPressureDelta(v), csv: (v) => csvPressureDelta(v), help: COLUMN_HELP.psi_inflated_estimated },
                                { key: 'deficit_fraction', label: t('hardcoded.pages_tpmreports.deficit_km_l_rend'), render: (v) => safeNumber(v, 0).toFixed(6), csv: (v) => csvFromNumber(v, 6), help: COLUMN_HELP.deficit_fraction },
                                { key: 'diesel_price_per_l', label: `${reportCurrency}/L`, render: (v) => formatNumber(v), csv: (v) => csvNumber(v), help: COLUMN_HELP.diesel_price_per_l },
                                { key: 'km_per_month', label: t('hardcoded.pages_tpmreports.km_mes_proy_30d'), render: (v) => formatNumber(v), csv: (v) => csvNumber(v), help: COLUMN_HELP.km_per_month },
                                { key: 'km_per_minute', label: t('hardcoded.pages_tpmreports.km_min'), render: (v) => safeNumber(v, 0).toFixed(4), csv: (v) => csvFromNumber(v, 6), help: COLUMN_HELP.km_per_minute },
                                { key: 'shifts_per_week', label: t('hardcoded.pages_tpmreports.turnos'), help: COLUMN_HELP.shifts_per_week },
                                { key: 'hours_per_shift', label: t('hardcoded.pages_tpmreports.horas_turno'), render: (v) => formatNumber(v), csv: (v) => csvNumber(v), help: COLUMN_HELP.hours_per_shift },
                                { key: 'previous_timestamp', label: t('hardcoded.pages_tpmreports.fecha_hora_anterior'), render: (v) => formatUserDateTime(v), csv: (v) => formatCsvDateTime(v), help: COLUMN_HELP.previous_timestamp },
                                { key: 'time_since_previous_label', label: t('hardcoded.pages_tpmreports.tiempo_vs_lectura_anterior'), help: COLUMN_HELP.time_since_previous },
                                { key: 'vehicle_km_per_l', label: t('hardcoded.pages_tpmreports.km_por_l'), render: (v) => safeNumber(v, 0).toFixed(2), csv: (v) => csvFromNumber(v, 4), help: COLUMN_HELP.vehicle_km_per_l },
                                { key: 'km_per_l_lost', label: t('hardcoded.pages_tpmreports.km_l_perdido'), render: (v) => safeNumber(v, 0).toFixed(6), csv: (v) => csvFromNumber(v, 6), help: COLUMN_HELP.km_per_l_lost },
                                { key: 'km_in_interval', label: t('hardcoded.pages_tpmreports.km_intervalo'), render: (v) => safeNumber(v, 0).toFixed(4), csv: (v) => csvFromNumber(v, 6), help: COLUMN_HELP.km_in_interval },
                                { key: 'liters_lost_interval', label: t('hardcoded.pages_tpmreports.l_perdidos'), render: (v) => safeNumber(v, 0).toFixed(6), csv: (v) => csvFromNumber(v, 6), help: COLUMN_HELP.liters_lost },
                                { key: 'cost_lost_interval', label: t('hardcoded.pages_tpmsreportspage.mxn_perdido_reportcurrency', { reportCurrency: reportCurrency }), render: (v) => formatCurrency(v, reportCurrency), csv: (v) => csvCurrency(v), help: COLUMN_HELP.cost_lost },
                                { key: 'fuel_penalty_pct', label: t('hardcoded.pages_tpmreports.penalizacion_porcentaje'), render: (v) => `${formatNumber(v)}%`, csv: (v) => csvPercent(v) },
                                { key: 'duration_hours', label: t('hardcoded.pages_tpmreports.duracion_h') },
                                { key: 'tire_loss_cost_interval', label: t('hardcoded.pages_tpmreports.costo_desgaste'), render: (v) => formatCurrency(v, reportCurrency), csv: (v) => csvCurrency(v) },
                                { key: 'severity', label: t('hardcoded.components_settings_notificationpreferenceseditor.severidad') },
                            ]}
                            defaultSort={{ key: 'cost_lost_interval', order: 'desc' }}
                            onExportCsv={(rows, cols) => exportLocalRows(rows, 'diesel_detalle_intervalos', cols)}
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <DataTable
                            title={t('hardcoded.pages_tpmsreportspage.impacto_por_sensor')}
                            rows={fuelSensorRows}
                            columns={[
                                { key: 'vehicle', label: t('hardcoded.pages_tpmreports.vehiculo') },
                                { key: 'sensor_id', label: t('hardcoded.pages_tpmreports.sensor') },
                                { key: 'duration_hours', label: t('hardcoded.pages_tpmreports.horas_bajo_ideal') },
                                { key: 'avg_deficit', label: t('hardcoded.pages_tpmsreportspage.deficit_promedio_pressuresymbol', { pressureSymbol: pressureSymbol }), render: (v) => formatPressureDelta(v), csv: (v) => csvPressureDelta(v) },
                                { key: 'max_deficit', label: `Deficit Maximo (${pressureSymbol})`, render: (v) => formatPressureDelta(v), csv: (v) => csvPressureDelta(v) },
                                { key: 'avg_penalty_pct', label: t('hardcoded.pages_tpmreports.penalizacion_porcentaje'), render: (v) => `${formatNumber(v)}%`, csv: (v) => csvPercent(v) },
                                { key: 'liters_extra', label: t('hardcoded.pages_tpmreports.col_litros_extra'), render: (v) => formatNumber(v), csv: (v) => csvNumber(v) },
                                { key: 'cost_extra', label: t('hardcoded.pages_tpmreports.costo_extra'), render: (v) => formatCurrency(v, reportCurrency), csv: (v) => csvCurrency(v) },
                            ]}
                            defaultSort={{ key: 'cost_extra', order: 'desc' }}
                            onExportCsv={(rows, cols) => exportLocalRows(rows, 'diesel_sensor', cols)}
                        />
                    </Grid>

                    <Grid item xs={12} md={3}>
                        <DataTable
                            title={t('hardcoded.pages_tpmsreportspage.ranking_por_eje')}
                            rows={fuelAxleRows}
                            columns={[
                                { key: 'vehicle', label: t('hardcoded.pages_tpmreports.vehiculo') },
                                { key: 'axle', label: t('hardcoded.pages_tpmreports.eje') },
                                { key: 'impact_index', label: t('hardcoded.pages_tpmreports.indice_impacto') },
                            ]}
                            defaultSort={{ key: 'impact_index', order: 'desc' }}
                            onExportCsv={(rows, cols) => exportLocalRows(rows, 'diesel_eje', cols)}
                        />
                    </Grid>

                    <Grid item xs={12} md={3}>
                        <DataTable
                            title={t('hardcoded.pages_tpmsreportspage.ranking_por_posicion')}
                            rows={fuelPositionRows}
                            columns={[
                                { key: 'vehicle', label: t('hardcoded.pages_tpmreports.vehiculo') },
                                { key: 'position', label: t('hardcoded.pages_tpmreports.posicion') },
                                { key: 'impact_index', label: t('hardcoded.pages_tpmreports.indice_impacto') },
                            ]}
                            defaultSort={{ key: 'impact_index', order: 'desc' }}
                            onExportCsv={(rows, cols) => exportLocalRows(rows, 'diesel_posicion', cols)}
                        />
                    </Grid>
                </Grid>
            )}

            {tab === 'tire' && (
                <DataTable
                    tableKey="tire_detail"
                    title={t('hardcoded.pages_tpmsreportspage.detalle_completo_de_rendimiento_por')}
                    rows={tireDetailRows}
                    columns={[
                        { key: 'timestamp', label: t('hardcoded.pages_tpmreports.fecha_hora'), render: (v) => formatUserDateTime(v), csv: (v) => formatCsvDateTime(v) },
                        { key: 'vehicle', label: t('hardcoded.pages_tpmreports.vehiculo') },
                        { key: 'company', label: t('hardcoded.pages_tpmreports.empresa') },
                        { key: 'sensor_id', label: t('hardcoded.pages_tpmreports.sensor') },
                        { key: 'axle', label: t('hardcoded.pages_tpmreports.eje') },
                        { key: 'position', label: t('hardcoded.pages_tpmreports.posicion') },
                        { key: 'cycle_id', label: t('hardcoded.pages_tpmreports.ciclo'), help: COLUMN_HELP.cycle_id },
                        { key: 'pressure_real_psi', label: t('hardcoded.pages_tpmsreportspage.presion_real_pressuresymbol', { pressureSymbol: pressureSymbol }), render: (v) => formatPressureReal(v), csv: (v) => csvPressureReal(v) },
                        { key: 'temperature_c', label: t('hardcoded.pages_tpmsreportspage.temp_temperaturesymbol', { temperatureSymbol: temperatureSymbol }), render: (v) => formatTemperatureValue(v), csv: (v) => csvTemperatureValue(v) },
                        { key: 'psi_ideal_profile', label: t('hardcoded.pages_tpmsreportspage.psi_ideal_perfil_pressuresymbol', { pressureSymbol: pressureSymbol }), render: (v, row) => formatPressureFromStored(v ?? row?.psi_ideal_base), csv: (v, row) => csvPressureFromStored(v ?? row?.psi_ideal_base), help: COLUMN_HELP.psi_ideal_profile },
                        { key: 'cold_pressure_start', label: t('hardcoded.pages_tpmsreportspage.presion_frio_inicio_viaje_pressuresymbol', { pressureSymbol: pressureSymbol }), render: (v) => formatPressureFromStored(v), csv: (v) => csvPressureFromStored(v), help: COLUMN_HELP.cold_pressure_start },
                        { key: 'cold_pressure_current', label: t('hardcoded.pages_tpmsreportspage.presion_frio_estimada_pressuresymbol', { pressureSymbol: pressureSymbol }), render: (v) => formatPressureFromStored(v), csv: (v) => csvPressureFromStored(v), help: COLUMN_HELP.cold_pressure_current },
                        { key: 'psi_ideal_variable', label: t('hardcoded.pages_tpmsreportspage.psi_ajustado_por_temp_pressuresymbol', { pressureSymbol: pressureSymbol }), render: (v) => formatPressureFromStored(v), csv: (v) => csvPressureFromStored(v), help: COLUMN_HELP.psi_ideal_variable },
                        { key: 'psi_deficit', label: t('hardcoded.pages_tpmsreportspage.deficit_psi_pressuresymbol', { pressureSymbol: pressureSymbol }), render: (v) => formatPressureDelta(v), csv: (v) => csvPressureDelta(v), help: COLUMN_HELP.psi_deficit },
                        { key: 'duration_hours', label: t('hardcoded.pages_tpmreports.duracion_h') },
                        { key: 'km_in_interval', label: t('hardcoded.pages_tpmreports.km_intervalo'), render: (v) => formatPerKm(v), csv: (v) => csvFromNumber(v, 5), help: COLUMN_HELP.km_in_interval },
                        { key: 'performance_pct', label: t('hardcoded.pages_tpmsreportspage.rendimiento_tabla'), render: (v) => `${formatNumber(v)}%`, csv: (v) => csvPercent(v), help: COLUMN_HELP.performance_pct_table },
                        { key: 'tire_perf_linear_pct', label: t('hardcoded.pages_tpmsreportspage.rendimiento_lineal'), render: (v) => `${formatNumber(v)}%`, csv: (v) => csvPercent(v), help: COLUMN_HELP.performance_linear },
                        { key: 'performance_loss_pct', label: t('hardcoded.pages_tpmreports.perdida_porcentaje'), render: (v) => `${formatNumber(v)}%`, csv: (v) => csvPercent(v), help: COLUMN_HELP.performance_loss },
                        { key: 'tire_loss_cost_interval', label: t('hardcoded.pages_tpmsreportspage.costo_desgaste_evento_por_duracion'), render: (v) => formatCurrency(v, reportCurrency), csv: (v) => csvCurrency(v), help: COLUMN_HELP.tire_loss_cost_interval },
                        // ---- Economia de llanta (km) ----
                        { key: 'tire_cost_per_tire', label: t('hardcoded.pages_tpmsreportspage.llanta_reportcurrency', { reportCurrency: reportCurrency }), render: (v) => formatCurrency(v, reportCurrency), csv: (v) => csvCurrency(v), help: COLUMN_HELP.tire_cost_per_tire },
                        { key: 'tire_total_km', label: t('hardcoded.pages_tpmreports.km_totales_llanta'), render: (v) => formatNumber(v), csv: (v) => csvFromNumber(v, 2), help: COLUMN_HELP.tire_total_km },
                        { key: 'tire_cost_per_km_full', label: t('hardcoded.pages_tpmsreportspage.km_al_100'), render: (v) => formatPerKm(v), csv: (v) => csvFromNumber(v, 6), help: COLUMN_HELP.tire_cost_per_km_full },
                        { key: 'tire_cost_per_km_table', label: t('hardcoded.pages_tpmsreportspage.km_al_desgaste_tabla'), render: (v) => formatPerKm(v), csv: (v) => csvFromNumber(v, 6), help: COLUMN_HELP.tire_cost_per_km_table },
                        { key: 'tire_cost_per_km_wasted_table', label: t('hardcoded.pages_tpmreports.dolar_km_desperdiciado_tabla'), render: (v) => formatPerKm(v), csv: (v) => csvFromNumber(v, 6), help: COLUMN_HELP.tire_cost_per_km_wasted_table },
                        { key: 'tire_wasted_interval_table', label: t('hardcoded.pages_tpmreports.dolar_llanta_desperdiciado_intervalo_tabla'), render: (v) => formatPerKm(v), csv: (v) => csvFromNumber(v, 4), help: COLUMN_HELP.tire_wasted_interval_table },
                        { key: 'tire_cost_per_km_linear', label: t('hardcoded.pages_tpmsreportspage.km_al_desgaste_lineal'), render: (v) => formatPerKm(v), csv: (v) => csvFromNumber(v, 6), help: COLUMN_HELP.tire_cost_per_km_linear },
                        { key: 'tire_cost_per_km_wasted_linear', label: t('hardcoded.pages_tpmreports.dolar_km_desperdiciado_lineal'), render: (v) => formatPerKm(v), csv: (v) => csvFromNumber(v, 6), help: COLUMN_HELP.tire_cost_per_km_wasted_linear },
                        { key: 'tire_wasted_interval_linear', label: t('hardcoded.pages_tpmreports.dolar_llanta_desperdiciado_intervalo_lineal'), render: (v) => formatPerKm(v), csv: (v) => csvFromNumber(v, 4), help: COLUMN_HELP.tire_wasted_interval_linear },
                        {
                            key: 'classification',
                            label: t('hardcoded.pages_tpmreports.clasificacion'),
                            render: (value) => {
                                const normalized = String(value || '').toLowerCase();
                                const color = normalized === 'excelente' ? 'success' : normalized === 'regular' ? 'warning' : 'error';
                                return <Chip size="small" color={color} label={value || '-'} />;
                            },
                            csv: (value) => String(value || ''),
                        },
                    ]}
                    defaultSort={{ key: 'tire_loss_cost_interval', order: 'desc' }}
                    onExportCsv={(rows, cols) => exportLocalRows(rows, 'desgaste_llantas_detalle', cols)}
                />
            )}


            {tab === 'compliance' && (
                <DataTable
                    title={t('hardcoded.pages_tpmsreportspage.cumplimiento_de_presion_por_vehiculo')}
                    rows={complianceRows}
                    columns={[
                        { key: 'vehicle', label: t('hardcoded.pages_tpmreports.vehiculo') },
                        { key: 'company', label: t('hardcoded.pages_tpmreports.empresa') },
                        { key: 'total_tires', label: t('hardcoded.pages_tpmreports.llantas') },
                        { key: 'ideal_pct', label: t('hardcoded.pages_tpmreports.porcentaje_ideal'), render: (v) => `${formatNumber(v)}%`, csv: (v) => csvPercent(v) },
                        { key: 'within_tolerance_pct', label: t('hardcoded.pages_tpmreports.porcentaje_tolerancia'), render: (v) => `${formatNumber(v)}%`, csv: (v) => csvPercent(v) },
                        { key: 'below_ideal_pct', label: t('hardcoded.pages_tpmreports.porcentaje_debajo'), render: (v) => `${formatNumber(v)}%`, csv: (v) => csvPercent(v) },
                        { key: 'above_ideal_pct', label: t('hardcoded.pages_tpmreports.porcentaje_encima'), render: (v) => `${formatNumber(v)}%`, csv: (v) => csvPercent(v) },
                    ]}
                    defaultSort={{ key: 'below_ideal_pct', order: 'desc' }}
                    onExportCsv={(rows, cols) => exportLocalRows(rows, 'cumplimiento_presion', cols)}
                />
            )}

            {tab === 'recurrence' && (
                <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                        <DataTable
                            title={t('hardcoded.pages_tpmsreportspage.reincidencia_por_vehiculo')}
                            rows={recurrence.vehicles || []}
                            columns={[
                                { key: 'entity', label: t('hardcoded.pages_tpmreports.vehiculo') },
                                { key: 'events', label: t('hardcoded.pages_tpmreports.eventos') },
                            ]}
                            defaultSort={{ key: 'events', order: 'desc' }}
                            onExportCsv={(rows, cols) => exportLocalRows(rows, 'reincidencia_vehiculos', cols)}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <DataTable
                            title={t('hardcoded.pages_tpmsreportspage.reincidencia_por_posicion')}
                            rows={recurrence.positions || []}
                            columns={[
                                { key: 'entity', label: t('hardcoded.pages_tpmreports.posicion') },
                                { key: 'events', label: t('hardcoded.pages_tpmreports.eventos') },
                            ]}
                            defaultSort={{ key: 'events', order: 'desc' }}
                            onExportCsv={(rows, cols) => exportLocalRows(rows, t('hardcoded.pages_tpmsreportspage.reincidencia_posiciones'), cols)}
                        />
                    </Grid>
                </Grid>
            )}

            {tab === 'resolution' && (
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <KPIGrid items={[
                            { label: t('hardcoded.pages_tpmreports.kpi_tiempo_promedio_resolucion'), value: `${formatNumber(resolution?.kpis?.avg_resolution_minutes)} min`, color: 'info.main' },
                            { label: t('hardcoded.pages_tpmreports.kpi_mttr'), value: `${formatNumber(resolution?.kpis?.mttr_minutes)} min`, color: 'info.main' },
                            { label: t('hardcoded.pages_tpmreports.backlog_abierto'), value: formatNumber(resolution?.kpis?.open_backlog), color: 'warning.main' },
                            { label: t('hardcoded.pages_tpmreports.tickets_resueltos'), value: formatNumber(resolution?.kpis?.resolved_tickets), color: 'success.main' },
                        ]} />
                    </Grid>
                    <Grid item xs={12}>
                        <DataTable
                            title={t('hardcoded.pages_tpmsreportspage.detalle_de_resolucion')}
                            rows={resolution.rows || []}
                            columns={[
                                { key: 'ticket_id', label: t('hardcoded.pages_tpmreports.ticket') },
                                { key: 'vehicle', label: t('hardcoded.pages_tpmreports.vehiculo') },
                                { key: 'metric_type', label: t('hardcoded.pages_tpmreports.metrica') },
                                { key: 'priority', label: t('hardcoded.components_tickets_ticketactioncenter.prioridad') },
                                { key: 'status', label: t('hardcoded.pages_tpmreports.estado') },
                                { key: 'resolution_type', label: t('hardcoded.pages_tpmreports.tipo_resolucion') },
                                { key: 'resolution_minutes', label: t('hardcoded.pages_tpmreports.minutos_resolucion') },
                                { key: 'created_at', label: t('hardcoded.pages_tpmreports.creado'), render: (v) => formatUserDateTime(v), csv: (v) => formatCsvDateTime(v) },
                                { key: 'resolved_at', label: t('hardcoded.pages_tpmreports.col_resuelto'), render: (v) => formatUserDateTime(v), csv: (v) => formatCsvDateTime(v) },
                            ]}
                            defaultSort={{ key: 'resolution_minutes', order: 'desc' }}
                            onExportCsv={(rows, cols) => exportLocalRows(rows, 'tiempo_resolucion', cols)}
                        />
                    </Grid>
                </Grid>
            )}

            {tab === 'overpressure' && (
                <DataTable
                    title={t('hardcoded.pages_tpmsreportspage.eventos_de_sobrepresion')}
                    rows={overpressureRows}
                    columns={[
                        { key: 'vehicle', label: t('hardcoded.pages_tpmreports.vehiculo') },
                        { key: 'axle', label: t('hardcoded.pages_tpmreports.eje') },
                        { key: 'position', label: t('hardcoded.pages_tpmreports.posicion') },
                        { key: 'original_alarm_value', label: t('hardcoded.pages_tpmsreportspage.valor_detectado_pressuresymbol', { pressureSymbol: pressureSymbol }), render: (v) => formatPressureFromStored(v), csv: (v) => csvPressureFromStored(v) },
                        { key: 'threshold_max', label: t('hardcoded.pages_tpmsreportspage.maximo_perfil_pressuresymbol', { pressureSymbol: pressureSymbol }), render: (v) => formatPressureFromStored(v), csv: (v) => csvPressureFromStored(v) },
                        { key: 'duration_hours', label: t('hardcoded.pages_tpmreports.horas_acumuladas') },
                        { key: 'priority', label: t('hardcoded.components_tickets_ticketactioncenter.prioridad') },
                        { key: 'status', label: t('hardcoded.pages_tpmreports.estado') },
                    ]}
                    defaultSort={{ key: 'duration_hours', order: 'desc' }}
                    onExportCsv={(rows, cols) => exportLocalRows(rows, 'sobrepresion', cols)}
                />
            )}

            {tab === 'temperature' && (
                <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                        <DataTable
                            title={t('hardcoded.pages_tpmsreportspage.eventos_de_temperatura')}
                            rows={temperatureEvents}
                            columns={[
                                { key: 'vehicle', label: t('hardcoded.pages_tpmreports.vehiculo') },
                                { key: 'axle', label: t('hardcoded.pages_tpmreports.eje') },
                                { key: 'position', label: t('hardcoded.pages_tpmreports.posicion') },
                                { key: 'temperature_value', label: t('hardcoded.pages_tpmsreportspage.temperatura_temperaturesymbol', { temperatureSymbol: temperatureSymbol }), render: (v) => formatTemperatureValue(v), csv: (v) => csvTemperatureValue(v) },
                                { key: 'duration_hours', label: t('hardcoded.pages_tpmreports.horas') },
                                { key: 'priority', label: t('hardcoded.components_tickets_ticketactioncenter.prioridad') },
                                { key: 'status', label: t('hardcoded.pages_tpmreports.estado') },
                            ]}
                            defaultSort={{ key: 'temperature_value', order: 'desc' }}
                            onExportCsv={(rows, cols) => exportLocalRows(rows, 'temperatura_eventos', cols)}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Paper sx={{ p: 2, height: '100%' }}>
                            <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>{t('hardcoded.pages_tpmsreportspage.estres_termico_por_unidad')}</Typography>
                            <HighchartsReact highcharts={Highcharts} options={temperatureStressOptions} />
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                                {i18n.t('hardcoded.pages_tpmsreportspage.correlacion_presiontemperatura')}{formatNumber(report?.temperature_report?.kpis?.pressure_temp_correlation)}
                            </Typography>
                        </Paper>
                    </Grid>
                </Grid>
            )}

            {tab === 'operating' && (
                <DataTable
                    title={t('hardcoded.pages_tpmsreportspage.tablero_operativo_de_salida')}
                    rows={operatingRows}
                    columns={[
                        { key: 'vehicle', label: t('hardcoded.pages_tpmreports.vehiculo') },
                        { key: 'company', label: t('hardcoded.pages_tpmreports.empresa') },
                        { key: 'critical_tires', label: t('hardcoded.pages_tpmreports.criticas') },
                        { key: 'low_tires', label: t('hardcoded.pages_tpmreports.baja_presion') },
                        { key: 'high_tires', label: t('hardcoded.pages_tpmreports.sobrepresion') },
                        { key: 'priority_score', label: t('hardcoded.pages_tpmsreportspage.score_prioridad') },
                        {
                            key: 'status',
                            label: t('hardcoded.pages_tpmreports.semaforo'),
                            render: (v) => <SemaforoChip status={v} />,
                            csv: (v) => {
                                const normalized = String(v || '').toLowerCase();
                                if (normalized.includes(t('hardcoded.pages_tpmsreportspage.no_debe'))) return t('hardcoded.pages_tpmsreportspage.no_debe_salir');
                                if (normalized.includes('observacion')) return t('hardcoded.pages_tpmsreportspage.sale_con_observacion');
                                return t('hardcoded.pages_tpmreports.puede_salir');
                            },
                        },
                    ]}
                    defaultSort={{ key: 'priority_score', order: 'asc' }}
                    onExportCsv={(rows, cols) => exportLocalRows(rows, 'estado_operativo', cols)}
                />
            )}

            <Dialog open={exportDialog.open} onClose={closeExportDialog} maxWidth="sm" fullWidth>
                <DialogTitle>{t('hardcoded.pages_tpmsreportspage.exportar_csv_elige_el_formato')}</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {t('hardcoded.pages_tpmsreportspage.el_separador_decimal_debe_coincidir')}</Typography>
                    <FormControl component="fieldset" fullWidth>
                        <FormLabel component="legend">{t('pages_tpmsreportspage.label_format')}</FormLabel>
                        <RadioGroup
                            value={exportFormat}
                            onChange={(e) => setExportFormat(e.target.value)}
                        >
                            {Object.values(CSV_FORMATS).map((fmt) => (
                                <FormControlLabel
                                    key={fmt.key}
                                    value={fmt.key}
                                    control={<Radio />}
                                    label={(
                                        <Box>
                                            <Typography variant="body1">{fmt.label}</Typography>
                                            <Typography variant="caption" color="text.secondary">{fmt.hint}</Typography>
                                        </Box>
                                    )}
                                    sx={{ alignItems: 'flex-start', mb: 1 }}
                                />
                            ))}
                        </RadioGroup>
                    </FormControl>
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeExportDialog} disabled={exporting}>{t('common.cancel')}</Button>
                    <Button variant="contained" startIcon={<DownloadIcon />} onClick={confirmExport} disabled={exporting}>
                        {i18n.t('hardcoded.pages_tpmsreportspage.descargar_csv')}</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default TPMSReportsPage;
